import { instrumentJourneyMutation, startJourneyOperation } from './journeyInstrumentation';
import {
  getStagingIdentityGeneration,
  isCurrentStagingIdentityGeneration,
  isStagingRumHost,
  setStagingRumUser,
} from './stagingRum';
import { assignmentTitleBaseline, assignmentTitleRevision } from './utils/assignmentTitle';
import { beginSubmissionRequest, checkSubmissionMaintenance, acknowledgeMaintenanceAnswer } from './utils/submissionMaintenance';
import React from 'react';
import { documentReviewIntent } from './utils/documentReview';
import { INSUFFICIENT_CREDITS } from './utils/feedbackCredits';
import {
  QUESTION_GENERATION_REFUSALS,
  questionGenerationError,
} from './utils/questionGeneration';
import { readFeedbackEvents } from './utils/readFeedbackEvents';
import { startRunWithDeadline } from './utils/runStartDeadline';
import {
  deleteProfileCookies,
  getAuthToken,
  getUserId,
  getUserRole,
  isStoredJwtExpired,
} from './userLocalDetails';
import { v4 as uuidv4 } from 'uuid';
import { getLocalStorage } from './utils/function';
import { normalizeAssignmentSelfAssessment } from './utils/selfAssessmentNormalization';
import { runAssignmentWrite, drainAssignmentWrites } from './service/assignmentWrites';
import {
  isEmbeddedMode,
  notifyEmbeddedSessionExpired,
  clearEmbeddedSession,
  clearLaunchContext,
} from './utils/ltiEmbedded';
import { toast } from 'react-toastify';
import Toast from './components/Toast';
import { downloadFileFromApi, fetchFileBlobFromApi, downloadBlobsAsZip } from './utils/download';
import { normalizeSubmissionFileUrls } from './utils/normalizeFileUrls';
import { teacherCheckHeadersFor } from './utils/teacherCheckScope';
import { endSessionKeepingTeacherLoop } from './utils/teacherLoopSession';
import { datadogRum } from '@datadog/browser-rum';
import { track as trackProductEvent } from './utils/productEvents';

// const baseUrl = process.env.REACT_APP_API_BASE_URL ?? "https://feedbacks-backend-leso2wocda-ts.a.run.app";
const baseUrl = (
  process.env.REACT_APP_API_BASE_URL ?? 'http://localhost:8080'
).replace(/\/+$/, '');
const jeddleBaseUrl =
  process.env.REACT_APP_JEDDLE_BASE_URL ?? 'https://jeddle.duxdigital.net';
const selfBaseUrl =
  process.env.REACT_APP_SELF_BASE_URL ?? 'http://localhost:1234';
const clientId =
  process.env.REACT_APP_CLIENT_ID ?? 'HyULSQEsXGFXmaOAzohGYFcvoegcxFaY';
const env = process.env.REACT_APP_ENV ?? 'dev';
const debugEnabled =
  (process.env.REACT_APP_DEBUG_LOADING ?? '').toLowerCase() === 'true' ||
  (process.env.NODE_ENV ?? '').toLowerCase() === 'development';
const logDebug = (...args) => {
  if (debugEnabled) {
    console.log('[service]', ...args);
  }
};


export let isLoggedOut = false;
let isRedirecting = false;
const COOLDOWN_PERIOD = 10000;

export const isLoggedIn = () => {
  const userId = localStorage.getItem('userId');
  // A provably-expired localStorage JWT means every guarded call is doomed to
  // 401 (and the 401 handler wipes storage + hard-redirects). Report logged-out
  // instead so prefetch bursts never fire on a dead session. Cookie-auth hosts
  // are unaffected: isStoredJwtExpired() is always false there.
  return userId !== null && userId !== undefined && !isStoredJwtExpired();
};

const normalizeYear = (y) => {
  if (typeof y === 'number' && !Number.isNaN(y)) return y;
  const s = String(y || '')
    .replace(/year\s*/i, '')
    .trim();
  const n = parseInt(s, 10);
  return Number.isNaN(n) ? undefined : n;
};

const toApiSubject = (s) =>
  s && String(s).toLowerCase() === 'unassigned' ? 'UNASSIGNED' : s;
const toApiYear = (y) =>
  y != null && String(y).toLowerCase() === 'unassigned'
    ? 'UNASSIGNED'
    : normalizeYear(y);
const enc = (v) => encodeURIComponent(String(v));
const buildQueryString = (filters = {}) => {
  const parts = [];
  const codeList =
    filters.subjectCodes ??
    (filters.subjectCode ? [filters.subjectCode] : []) ??
    filters.subjects ??
    (filters.subject ? [filters.subject] : []);
  const codes = codeList.filter((s) => s).map((s) => toApiSubject(s));
  if (codes.length === 1) parts.push('subjectCode=' + enc(codes[0]));
  else codes.forEach((c) => parts.push('subjectCodes=' + enc(c)));
  codes.forEach((s) => s && parts.push('subjects=' + enc(s)));
  const statesArr = filters.states || (filters.state ? [filters.state] : []);
  if (statesArr.length === 1) parts.push('state=' + enc(statesArr[0]));
  else statesArr.forEach((s) => s && parts.push('states=' + enc(s)));
  const yearsRaw =
    filters.years ??
    (filters.year !== undefined && filters.year !== null ? [filters.year] : []);
  const yearsProcessed = [...yearsRaw].map(toApiYear);
  yearsProcessed.forEach((y) => y != null && parts.push('years=' + enc(y)));
  const taskTypes =
    filters.taskTypes || (filters.taskType ? [filters.taskType] : []);
  taskTypes.forEach((t) => t && parts.push('taskTypes=' + enc(t)));
  const responseTypes =
    filters.responseTypes ||
    (filters.responseType ? [filters.responseType] : []);
  responseTypes.forEach((r) => r && parts.push('responseTypes=' + enc(r)));
  const order =
    filters.sortOrder === 'newToOld' || filters.sortOrder === 'oldToNew'
      ? filters.sortOrder
      : null;
  if (order) parts.push('sortOrder=' + enc(order));
  return parts.length ? '?' + parts.join('&') : '';
};

const isTeacher = (role = null) => {
  const userRole = role || getUserRole();
  return Boolean(userRole) && String(userRole).toUpperCase() === 'TEACHER';
};
// Error bodies that follow the {code, message, details} envelope (see
// docs/bulk-drafts-api-spec.html §Errors) carry the actionable part outside
// `message` — details.studentId is what lets a caller name the offending row.
// Attach the whole envelope to the Error so nothing is lost on the way up;
// `message` is unchanged, so callers that only read that are unaffected.
const apiError = (message, status, body) =>
  Object.assign(new Error(message), {
    status,
    code: body?.code ?? null,
    details: body?.details ?? null,
    body: body ?? null,
  });

const parseJsonSafely = async (response) => {
  try {
    return await response.json();
  } catch {
    return null;
  }
};

const finishAuthHandoff = (options) => {
  if (options.throwOnError) {
    throw Object.assign(apiError('Your session has expired.', 401, { code: 'SESSION_EXPIRED' }), { authHandled: true });
  }
};

async function fetchData(url, options = {}, headers = {}) {
  const maintenanceTicket = beginSubmissionRequest();
  const token = getAuthToken();
  const suppressToast = options.suppressToast || false;

  const createHeaders = () => {
    return {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      // Teacher Check page only: its submission's reads carry the check token.
      // Adds nothing anywhere else (and nothing at all with the flag off).
      ...teacherCheckHeadersFor(url),
      ...headers,
    };
  };

  try {
    const response = await fetch(url, {
      ...options,
      credentials: options.credentials ?? 'include',
      headers: createHeaders(),
    });

    // Get error message from header if present
    await checkSubmissionMaintenance(response, url, options, maintenanceTicket);

    const errorMessage = response.headers.get('x-error-message');
    const redirectUrl = response.headers.get('x-redirect-url');

    if (!response.ok) {
      console.error('API request failed:', response);
      if (response.status === 401) {
        if (isEmbeddedMode()) {
          // Embedded in an LMS iframe: do NOT wipe storage or navigate the frame.
          // The LMS owns the session; surface a "relaunch from your LMS" state instead.
          notifyEmbeddedSessionExpired();
          return finishAuthHandoff(options);
        }
        if (process.env.REACT_APP_TEACHER_LOOP === 'on') {
          // Keeps the jeddle.tl.* keys; on a Teacher Loop public page, stays
          // and reloads logged out (utils/teacherLoopSession.js).
          if (!endSessionKeepingTeacherLoop(deleteProfileCookies)) {
            window.location.href = '/';
          }
          return finishAuthHandoff(options);
        }
        deleteProfileCookies();
        localStorage.clear();
        window.location.href = '/';
        return finishAuthHandoff(options);
      }

      // Show toast if there's an error message in header
      if (errorMessage && !suppressToast) {
        toast(<Toast message={errorMessage} />);
      }

      if (!options.suppressRedirect) handleRedirect(redirectUrl);

      const errorData = await parseJsonSafely(response);
      throw apiError(
        errorData?.message || 'API request failed',
        response.status,
        errorData,
      );
    }

    return response.json();
  } catch (error) {
    console.error('Fetch error:', error);
    throw error;
  }
}
const isLocalhost = () =>
  typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1');

const handleRedirect = (redirectUrl, immediate = false) => {
  if (!redirectUrl) return;
  if (isLocalhost()) {
    try {
      const redirectHost = new URL(redirectUrl.replace(/['"]+/g, '')).hostname;
      if (redirectHost !== 'localhost' && redirectHost !== '127.0.0.1') return;
    } catch {
      return;
    }
  }
  const cleanUrl = redirectUrl.replace(/['"]+/g, '');
  const go = () => {
    if (window.location.href === cleanUrl) {
      window.location.reload();
    } else {
      window.location.href = cleanUrl;
    }
  };
  // Intentional 4s delay lets a success/error toast be read before navigating
  // away. Callers that are a pure redirect (e.g. upgrade checkout) pass
  // immediate=true to skip the wait — otherwise the user stares at a frozen
  // modal for 4s after clicking (e.g. the referral "Skip" button).
  if (immediate) {
    go();
  } else {
    setTimeout(go, 4000);
  }
};
async function modifyDataRaw(url, options = {}) {
  const maintenanceTicket = beginSubmissionRequest();
  const token = getAuthToken();
  const suppressToast = options.suppressToast || false;
  const errorToastState = { shown: false };

  const createHeaders = () => {
    return {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    };
  };

  try {
    const response = await fetch(url, {
      ...options,
      credentials: options.credentials ?? 'include',
      headers: createHeaders(),
    });

    // Get messages and redirect URL from headers
    await checkSubmissionMaintenance(response, url, options, maintenanceTicket);

    const errorMessage = response.headers.get('x-error-message');
    const successMessage = response.headers.get('x-success-message');
    const redirectUrl = response.headers.get('x-redirect-url');
    const forceRefresh = response.headers.get('x-force-refresh');

    if (!response.ok) {
      // Embedded in an LMS iframe + 401: do NOT wipe storage, reload the frame,
      // navigate via x-redirect-url, or toast — any of those would tear down /
      // navigate the iframe before the "relaunch from your LMS" notice can show.
      // The LMS owns the session; surface the relaunch state instead. This must
      // run BEFORE the force-refresh / redirect / toast handling below.
      if (response.status === 401 && isEmbeddedMode()) {
        notifyEmbeddedSessionExpired();
        return finishAuthHandoff(options);
      }

      const errorData = await parseJsonSafely(response);
      const finalErrorMessage =
        errorMessage ||
        errorData?.message ||
        `Error occurred: ${response.status}`;
      // Show error toast. A refusal code the caller handles itself
      // (quietErrorCodes) gets no toast, and none from the catch below either.
      if (!suppressToast) {
        if (!options.quietErrorCodes?.includes(errorData?.code)) {
          toast(<Toast message={finalErrorMessage} />);
        }
        errorToastState.shown = true;
      }
      if (forceRefresh && forceRefresh === 'true') {
        window.location.reload();
      }
      // Handle redirect
      handleRedirect(redirectUrl);

      if (response.status === 401) {
        if (process.env.REACT_APP_TEACHER_LOOP === 'on') {
          // Keeps the jeddle.tl.* keys; on a Teacher Loop public page, stays
          // and reloads logged out (utils/teacherLoopSession.js).
          if (!endSessionKeepingTeacherLoop(deleteProfileCookies)) {
            window.location.href = '/';
          }
          return finishAuthHandoff(options);
        }
        deleteProfileCookies();
        localStorage.clear();
        window.location.href = '/';
        return finishAuthHandoff(options);
      }

      throw apiError(finalErrorMessage, response.status, errorData);
    }

    // Handle success case
    if (successMessage && !suppressToast) {
      toast(<Toast message={successMessage} type="success" />);
    }

    // Handle redirect for successful responses
    handleRedirect(redirectUrl, options.immediateRedirect === true);

    const responseText = await response.text();
    let responseData = null;
    if (responseText && responseText.trim() !== '') {
      try {
        responseData = JSON.parse(responseText);
      } catch (parseError) {
        if (options.throwOnError) throw parseError;
        responseData = null;
      }
    }

    if (Array.isArray(responseData?.answers)) {
      acknowledgeMaintenanceAnswer(url, options, maintenanceTicket, responseData);
    }

    // status and headers ride along because the body alone cannot always answer
    // what happened — an idempotent replay returns the original body verbatim.
    return {
      data: responseData,
      status: response.status,
      headers: response.headers,
      successMessage: successMessage,
      errorMessage: null,
    };
  } catch (error) {
    console.error('Fetch error:', error);
    if (!suppressToast && !errorToastState.shown && !error.authHandled && !error.maintenanceHandled) {
      toast(<Toast message={error.message} />);
    }
    throw error;
  }
}

const modifyData = (url, options = {}, correlationOptions = {}) =>
  instrumentJourneyMutation(
    url, options.method,
    (correlation) => modifyDataRaw(
      url,
      correlation ? { ...options, headers: { ...options.headers, ...correlation } } : options
    ),
    correlationOptions
  );

const fetchApi = async (url, options, headers) => {
  return fetchData(url, options, headers);
};

const getApi = async (url, options = {}) =>
  fetchApi(url, { ...options, method: 'GET' });

export const postApi = async (url, body, options = {}) => {
  try {
    const response = await modifyData(
      url,
      {
        method: 'POST',
        body: JSON.stringify(body),
        suppressToast: options.suppressToast || false,
        throwOnError: options.throwOnError || false,
        signal: options.signal,
        headers: options.headers,
        quietErrorCodes: options.quietErrorCodes,
      },
      { feedbackOperationId: body?.operationId }
    );

    if (options.includeMessages) {
      return response;
    }
    return response?.data;
  } catch (error) {
    if (error.maintenanceHandled) throw error;
    console.error('Post API error:', error);
    if (options.throwOnError) throw error;
    if (options.includeMessages) {
      return {
        data: null,
        successMessage: null,
        errorMessage: error.message,
        status: error.status ?? null,
        errorCode: error.code ?? null,
      };
    }
    return null;
  }
};
const putApi = async (url, body, options = {}) => {
  const token = getAuthToken();

  const response = await modifyData(url, {
    ...options,
    method: 'PUT',
    body: JSON.stringify(body),
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...options.headers,
    },
  });
  return response?.data;
};
const patchApi = async (url, body, options = {}) => {
  const token = getAuthToken();

  const response = await modifyData(url, {
    ...options,
    method: 'PATCH',
    body: JSON.stringify(body),
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...options.headers,
    },
  });
  return response?.data;
};

const deleteApi = async (url, options = {}) => {
  const token = getAuthToken();

  const response = await modifyData(url, {
    ...options,
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...options.headers,
    },
  });
  return response?.data;
};

const normalizeDownloadFormat = (format = 'docx') =>
  String(format).toLowerCase() === 'pdf' ? 'pdf' : 'docx';

const withDownloadFormat = (url, format) => {
  const normalized = normalizeDownloadFormat(format);
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}format=${normalized}`;
};

export const downloadSubmission = async (submissionId, format = 'docx') => {
  const fileFormat = normalizeDownloadFormat(format);
  const url = withDownloadFormat(
    `${baseUrl}/submissions/${submissionId}/download`,
    fileFormat,
  );
  const token = getAuthToken();
  await downloadFileFromApi({
    url,
    token,
    filename: `submission-${submissionId}.${fileFormat}`,
  });
};

/**
 * Download selected submissions as one ZIP (client-side) when multiple are chosen.
 * Single selection downloads the file directly.
 * @param {string[]} submissionIds
 * @param {string} [zipFilename]
 * @param {string} [format]
 */
export const downloadSubmissionsAsZip = async (
  submissionIds = [],
  zipFilename = 'submissions',
  format = 'docx',
) => {
  const token = getAuthToken();
  const ids = submissionIds.filter(Boolean);
  const fileFormat = normalizeDownloadFormat(format);

  if (ids.length === 0) return;
  if (ids.length === 1) {
    await downloadSubmission(ids[0], fileFormat);
    return;
  }

  const files = [];
  for (const submissionId of ids) {
    const url = withDownloadFormat(
      `${baseUrl}/submissions/${submissionId}/download`,
      fileFormat,
    );
    const file = await fetchFileBlobFromApi({
      url,
      token,
      filename: `submission-${submissionId}.${fileFormat}`,
    });
    files.push(file);
  }
  await downloadBlobsAsZip(files, zipFilename);
};

/**
 * Download all submissions ZIP for a specific assignment.
 * @param {string} assignmentId
 * @param {string} [format]
 */
export const downloadAllSubmissions = async (assignmentId, format = 'docx') => {
  const url = withDownloadFormat(
    `${baseUrl}/submissions/${assignmentId}/downloadAll`,
    format,
  );
  const token = getAuthToken();
  await downloadFileFromApi({
    url,
    token,
    filename: `assignment-${assignmentId}-submissions.zip`,
  });
};

export const deleteFeedback = async (submissionId, commentId, options = {}) => {
  return deleteApi(
    baseUrl + '/submissions/' + submissionId + '/feedbacks/' + commentId,
    options,
  );
};

export const resetFeedback = async (submissionId) => {
  return deleteApi(baseUrl + '/submissions/' + submissionId + '/feedbacks');
};

export const deleteFocusArea = async (focusAreaID) => {
  return deleteApi(baseUrl + '/feedbacks' + '/focusAreas/' + focusAreaID);
};

export const logout = async () => {
  logoutLocal();
  datadogRum.clearUser();
  // from_app=1 lets WordPress skip its "Do you really want to log out?" page
  // for this app (JED-649); without that WP change it still shows the page.
  const logoutUrl = `${jeddleBaseUrl}/wp-login.php?action=logout&from_app=1`;
  window.location.href = logoutUrl;
};

export const goToStudyGuides = () => {
  window.open(jeddleBaseUrl + '/my-courses');
};

export const reportIssue = async (reasons, text, role) => {
  // HashRouter keeps the in-app route in the hash (e.g. "#/tasks/123/feedback").
  // Send the real route so support can tell which screen the report came from;
  // this was previously hardcoded to 'global', which made the field useless.
  // Query params are stripped: hash routes can carry one-time credentials
  // (e.g. ?lti_code=...) that must not be persisted or emailed.
  const route = (
    window.location.hash
      ? window.location.hash.replace(/^#/, '')
      : window.location.pathname
  ).split('?')[0];
  const body = {
    location: route || 'global',
    reasons,
    text,
    role,
  };

  return await postApi(`${baseUrl}/user-feedback/report`, body);
};

/**
 * "Request a quote" for a school plan. Emails the sales inbox straight away (so a teacher who closes
 * the enquiry form is still followed up) and resolves what the server knows to prefill that form:
 * `{ notified, prefill: { firstName, lastName, email, schoolName, phone, subjects } }`.
 * Resolves `null` when the request failed and `undefined` on an expired session.
 */
export const requestSchoolPlan = async ({ source = '', trigger = '' } = {}) => {
  const result = await postApi(
    `${baseUrl}/users/school-plan-request`,
    { source, trigger },
    { includeMessages: true, suppressToast: true }
  );
  if (!result || result.errorMessage) return result === undefined ? undefined : null;
  return result.data ?? null;
};

/**
 * The submitted school plan enquiry form. Resolves `true` once the sales inbox has the email;
 * otherwise `{ errorMessage }` (or `undefined` on an expired session), so the form can say it
 * was not sent.
 */
export const submitSchoolPlanEnquiry = async (enquiry) => {
  const result = await postApi(`${baseUrl}/users/school-plan-request/details`, enquiry, {
    includeMessages: true,
    suppressToast: true,
  });
  if (result === undefined) return undefined;
  if (!result || result.errorMessage) {
    return { errorMessage: result?.errorMessage || 'Request failed' };
  }
  return true;
};

/**
 * Has this student already registered interest in tutoring (JED-1486)? Resolves
 * `{ registered, registeredAt? }`, plus `prefill: { name, email }` from the account when asked for (the
 * form opening; the card's check doesn't ask, so it costs no WordPress read). Rejects on failure (getApi),
 * so a caller that can't tell keeps offering the form (the backend keeps one lead per student anyway).
 */
export const getTutoringInterest = async ({ prefill = false } = {}) =>
  getApi(
    `${baseUrl}/users/tutoring-interest${prefill ? '?prefill=true' : ''}`,
    { suppressRedirect: true, suppressToast: true }
  );

/**
 * The tutoring interest form. Resolves:
 * - `{ registered: true, alreadyRegistered, registeredAt }` when the backend stored the lead (or had the
 *   student's earlier one);
 * - `{ error: 'invalid', field, message }` on a 400 (`field` is the form field it refused, when it says);
 * - `{ error: 'forbidden' }` on a 403 (not a student account);
 * - `{ error: 'failed' }` on a network error, a 5xx, or a 2xx that doesn't say it stored the lead;
 * - `undefined` when the session expired (service.js is already sending them to sign in).
 * Rejects only for maintenance mode, which service.js shows itself.
 */
export const registerTutoringInterest = async (interest) => {
  try {
    const data = await postApi(`${baseUrl}/users/tutoring-interest`, interest, {
      throwOnError: true,
      suppressToast: true,
    });
    return data?.registered === true ? data : { error: 'failed' };
  } catch (error) {
    if (error?.maintenanceHandled) throw error;
    if (error?.authHandled) return undefined;
    if (error?.status === 400) {
      return {
        error: 'invalid',
        field: error.body?.field ?? null,
        message: error.body?.message ?? null,
      };
    }
    if (error?.status === 403) return { error: 'forbidden' };
    return { error: 'failed' };
  }
};

export const changePassword = async () => {
  window.open(jeddleBaseUrl + '/account/?action=newpassword');
};
export const account = async () => {
  window.open(jeddleBaseUrl + '/account');
};
export const upgradeCheckout = async (
  planType = null,
  billingCycle = null,
  coupon = null,
) => {
  const params = new URLSearchParams();
  if (planType) {
    params.append('planType', planType);
  }
  if (billingCycle) {
    params.append('billingCycle', billingCycle);
  }
  if (coupon) {
    params.append('coupon', coupon);
  }
  const queryString = params.toString();
  const url = queryString
    ? `${baseUrl}/upgrade/checkout?${queryString}`
    : `${baseUrl}/upgrade/checkout`;
  // immediateRedirect: skip handleRedirect's 4s toast delay — this call is a
  // pure hand-off to the WooCommerce cart, so navigate the moment the backend
  // returns the x-redirect-url.
  const result = await modifyData(url, { method: 'GET', immediateRedirect: true, throwOnError: true, suppressToast: true });
  if (!result?.headers?.get('x-redirect-url')) throw new Error('Checkout is unavailable. Please try again.');
};
export const getTasks = async () => await getApi(baseUrl + '/tasks');
export const getIsJeddAIEnabled = async () => {
  if (isLoggedIn()) {
    return await getApi(baseUrl + '/users/isJeddAIEnabled');
  } else {
    return false;
  }
};

const buildFeedbackFiltersQueryParams = (filters = {}) => {
  const queryParams = new URLSearchParams();

  queryParams.append('page', filters.page || '0');
  queryParams.append('size', filters.size || '10');

  if (filters.sortDirection)
    queryParams.append('sortDirection', filters.sortDirection);
  if (filters.sortBy) queryParams.append('sortBy', filters.sortBy);
  if (filters.state?.trim()) queryParams.append('state', filters.state.trim());
  if (filters.year?.trim()) queryParams.append('year', filters.year.trim());
  if (filters.subject?.trim())
    queryParams.append('subject', filters.subject.trim());
  if (filters.documentType?.trim())
    queryParams.append('documentType', filters.documentType.trim());
  if (filters.taskType?.trim())
    queryParams.append('taskType', filters.taskType.trim());
  if (filters.responseType?.trim())
    queryParams.append('responseType', filters.responseType.trim());
  (filters.taskTypes || [])
    .filter(Boolean)
    .forEach((t) => queryParams.append('taskTypes', t));
  (filters.responseTypes || [])
    .filter(Boolean)
    .forEach((r) => queryParams.append('responseTypes', r));

  return queryParams.toString();
};

export const getCommunityTasks = async (filters = {}) => {
  if (!isLoggedIn()) {
    return [];
  }

  const queryString = buildFeedbackFiltersQueryParams(filters);
  const url = `${baseUrl}/communityTasks?${queryString}`;
  return await getApi(url);
};

export const getGiveFeedbackCompletedTasks = async (filters = {}) => {
  if (!isLoggedIn()) {
    return [];
  }

  const queryString = buildFeedbackFiltersQueryParams(filters);
  const url = `${baseUrl}/feedbackHistory?${queryString}`;
  return await getApi(url);
};
// Round 7 follow-up cleanup: getClassesWithStudents +
// getActiveClassesWithStudents removed (zero callers).
//
// Both wrapped /classes/all/details which is now @Deprecated server-side.
// The replacement is getClassesWithMembers (below) which calls the
// batched /classes/with-members endpoint and folds class metadata,
// students, AND teachers into a single HTTP round-trip.
//
// Active-only filtering moved into the hook layer
// (components/state/hooks.js -> useActiveClassData) so callers don't
// need to import filterActiveClasses themselves.

/**
 * Single-call replacement for `getClassesWithStudents` + N x `getTeachersForClass`.
 *
 * Calls the backend's `/classes/with-members` endpoint which collapses the
 * per-class triple fan-out (class details + students + teachers) into ONE
 * HTTP request. Members are name-only (no email/avatar/role) — the only
 * frontend consumers reading those fields go through the singular
 * `/classes/{id}/students` and `/classes/{id}/teachers` endpoints.
 *
 * Returns { classes: [...], degraded: bool }. The `degraded` flag is true
 * when the backend's batch returned partial data (transient WP outage) —
 * callers can show a "data may be incomplete" banner.
 */
export const getClassesWithMembers = async () => {
  if (!isLoggedIn()) {
    return { classes: [], degraded: false };
  }
  const resp = await getApi(baseUrl + '/classes/with-members');
  return resp || { classes: [], degraded: false };
};
export const getModelResponses = async () =>
  await getApi(baseUrl + '/feedbacks/modelResponses/summary');
export const getCompletedTasks = async () => {
  if (!isLoggedIn()) {
    return [];
  }
  if (isTeacher()) {
    return [];
  }
  return await getApi(baseUrl + '/completed-tasks');
};
// Every role: GET /notifications builds the student feed too (new tasks,
// reviewed work, peer reviews). It is not polled — fetched once per session and
// refetched only when a task action invalidates ['notifications'].
export const getNotifications = async () => {
  if (!isLoggedIn()) {
    return [];
  }
  return await getApi(baseUrl + '/notifications');
};
export const getAssignmentById = async (id, options = {}) => {
  if (!id) return null;
  return await getApi(baseUrl + '/assignments/' + id, options);
};
export const getAssignmentsByClassId = async (classId) =>
  await getApi(baseUrl + '/classes/' + classId + '/assignments');
export const getStudentsAnalyticsByClassId = async (classId) =>
  await getApi(baseUrl + '/feedbacks/' + classId + '/studentsAnalytics');
export const getStudentsStatsByClassId = async (classId) =>
  await getApi(baseUrl + '/classes/' + classId + '/studentStats');
export const getAssignments = async (subjectCode) => {
  const code =
    subjectCode && String(subjectCode).toLowerCase() === 'unassigned'
      ? 'UNASSIGNED'
      : subjectCode;
  const url = code
    ? `${baseUrl}/assignments?subjectCode=${encodeURIComponent(code)}`
    : `${baseUrl}/assignments`;
  return await getApi(url);
};
export const getDocuments = async () => {
  if (!isLoggedIn()) return [];
  return getApi(`${baseUrl}/submissions/documents`);
};
export const getDocumentReviews = async () =>
  await getApi(baseUrl + '/document-review-tasks');
export const startSubmission = async (assignmentDetails) =>
  await postApi(baseUrl + '/submissions', assignmentDetails);

/**
 * Nudge a student who has not started a task. Sends an email and nothing else —
 * no submission is created, so the student's row stays "not started".
 *
 * postApi swallows failures into null, and the caller has to tell "queued" from
 * "never sent" to know whether it may show the notified screen; hence
 * includeMessages + an explicit throw. Toasting is left to the caller so the
 * message names the action.
 */
export const notifyStudentToSubmit = async (assignmentId, studentId) => {
  const result = await postApi(
    `${baseUrl}/assignments/${assignmentId}/students/${studentId}/notify`,
    {},
    { includeMessages: true, suppressToast: true },
  );
  if (!result || result.errorMessage) {
    throw new Error(result?.errorMessage || 'Failed to notify student');
  }
  return result.data;
};
export const nextSubmission = async (assignmentID) =>
  await getApi(baseUrl + '/assignments/' + assignmentID + '/nextSubmission');

export const getSubmissionById = async (submissionId, options = {}) => {
  if (!submissionId) return null;
  const submission = await getApi(baseUrl + '/submissions/' + submissionId, options);
  return normalizeSubmissionFileUrls(submission);
};
export const getSubmissionVersions = async (submissionId) => {
  if (!submissionId) return [];
  return await getApi(baseUrl + '/submissions/' + submissionId + '/versions');
};

// The API serialises with NON_EMPTY, so a submission with no earlier attempts
// answers `{}` rather than `{ attempts: [] }` (JED-1389); hand callers a fixed shape.
export const getClassAttemptHistory = async (submissionId, before, options = {}) => {
  const page = await getApi(`${baseUrl}/submissions/${enc(submissionId)}/attempts${before != null ? `?before=${enc(before)}` : ''}`, options);
  return {
    attempts: Array.isArray(page?.attempts) ? page.attempts : [],
    nextBefore: page?.nextBefore ?? null,
  };
};

const getClassAttemptSection = async (submissionId, attemptId, section, options) => {
  const chunks = [];
  let after = 0;
  do {
    const page = await getApi(`${baseUrl}/submissions/${enc(submissionId)}/attempts/${enc(attemptId)}/parts?section=${section}&after=${after}`, options);
    // NON_EMPTY serialisation drops an empty chunk list, so a section with
    // nothing recorded (total 0) arrives as `{ total: 0 }`.
    const pageChunks = Array.isArray(page?.chunks) ? page.chunks : page?.total === 0 ? [] : null;
    if (!pageChunks || (page.nextAfter != null && page.nextAfter <= after))
      throw new Error('Historical content is unavailable');
    chunks.push(...pageChunks);
    after = page.nextAfter ?? null;
  } while (after != null);
  return chunks.join('').split('\n').filter(Boolean).map((record) => JSON.parse(record));
};

export const getClassAttemptSnapshot = async (submissionId, attemptId, options = {}) => {
  const summary = await getApi(`${baseUrl}/submissions/${enc(submissionId)}/attempts/${enc(attemptId)}`, options);
  if (summary?.id !== attemptId || (summary.submissionId != null && String(summary.submissionId) !== String(submissionId))) throw new Error('Historical attempt changed');
  const [answers, questions, feedback] = await Promise.all([
    getClassAttemptSection(submissionId, attemptId, 'answers', options),
    getClassAttemptSection(submissionId, attemptId, 'questions', options),
    summary.feedbackAvailable ? getClassAttemptSection(submissionId, attemptId, 'feedback', options) : [],
  ]);
  return { ...summary, submissionId, answers, questions, feedback };
};
export const getSubmissionVersionDetails = async (
  submissionId,
  version,
  options = {}
) => {
  if (!submissionId || version === undefined || version === null) return null;
  return await getApi(
    baseUrl + '/submissions/' + submissionId + '/versions/' + version,
    options
  );
};
export const restoreSubmissionVersion = async (submissionId, version) => {
  if (!submissionId || version === undefined || version === null) return null;
  return await patchApi(
    baseUrl +
      '/submissions/' +
      submissionId +
      '/versions/' +
      version +
      '/restore',
  );
};
export const getAcknowledgementsForSubmission = async (
  submissionId,
  version,
) => {
  if (!submissionId) return [];
  const versionQuery =
    version !== undefined && version !== null ? `?version=${version}` : '';
  return await getApi(
    baseUrl +
      '/submissions/' +
      submissionId +
      '/acknowledgements' +
      versionQuery,
  );
};
export const getSubmissionsByAssignmentId = async (
  assignmentId,
  { requireValidResponse = false } = {},
) => {
  if (!assignmentId) {
    logDebug('getSubmissionsByAssignmentId skipped: missing assignmentId');
    return [];
  }
  logDebug('getSubmissionsByAssignmentId', assignmentId);
  const payload = await getApi(
    baseUrl + '/assignments/' + assignmentId + '/submissions',
  );
  const submissions = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.submissions)
      ? payload.submissions
      : Array.isArray(payload?.data)
        ? payload.data
        : null;
  if (requireValidResponse && submissions === null) {
    throw new Error('Could not load task responses');
  }
  return (submissions || []).map(normalizeSubmissionFileUrls);
};
export const getOverComments = async (params, options = {}) => {
  const submissionId =
    typeof params === 'object' ? params?.submissionId : params;
  if (!submissionId) {
    return [];
  }
  const version = typeof params === 'object' ? params?.version : null;
  const versionQuery =
    version !== undefined && version !== null ? `?version=${version}` : '';
  return await getApi(
    baseUrl +
      '/submissions/' +
      submissionId +
      '/overallComments' +
      versionQuery,
    options,
  );
};
export const getPortfolioSubjects = async () =>
  await getApi(baseUrl + '/students/drafts');

export const addFeedback = async (submissionId, comment, options = {}) =>
  await postApi(
    baseUrl + '/submissions/' + submissionId + '/feedbacks',
    comment,
    options,
  );
export const deleteSubmissionById = async (submissionId) => {
  await patchApi(baseUrl + '/submissions/' + submissionId + '/delete');
};

export const updateFeedback = async (submissionId, commentId, comment, options = {}) =>
  await putApi(
    baseUrl + '/submissions/' + submissionId + '/feedbacks/' + commentId,
    comment,
    options,
  );

export const resolveFeedback = async (feedbackId) =>
  await patchApi(baseUrl + '/feedbacks/comment/' + feedbackId + '/resolve');

export const provideFeedbackOnFeedback = async (
  submissionId,
  feedbackOnFeedback,
) =>
  await patchApi(
    baseUrl +
      '/submissions/' +
      submissionId +
      '/feedbackOnFeedback/' +
      feedbackOnFeedback,
  );

export const getSmartAnnotaionAnalyticsByClassId = async (classId) => {
  let smartAnnotationsMap = new Map();
  const feedbacks = await getApi(
    baseUrl + '/feedbacks/' + classId + '/analytics',
  );
  await feedbacks.forEach((feedback) => {
    const splits = feedback.comment.split('\n\n');
    const title = splits[0].trim();
    const suggestion = splits[1]?.trim();
    if (smartAnnotationsMap.get(title)) {
      if (smartAnnotationsMap.get(title).get(suggestion)) {
        smartAnnotationsMap
          .get(title)
          .set(suggestion, smartAnnotationsMap.get(title).get(suggestion) + 1);
      } else {
        smartAnnotationsMap.get(title).set(suggestion, 1);
      }
    } else {
      smartAnnotationsMap.set(title, new Map([[suggestion, 1]]));
    }
  });
  return smartAnnotationsMap;
};

export const createNewMarkingCriteria = async (markingCriteria) => {
  console.log('[copy rubric] API request', {
    url: baseUrl + '/teachers/markingCriteria',
    payload: markingCriteria,
  });
  const res = await postApi(
    baseUrl + '/teachers/markingCriteria',
    markingCriteria,
  );
  console.log('[copy rubric] API response', res);
  return res;
};
// Rejects on failure (postApi would resolve null, which callers read as a
// saved rubric), and leaves the toast to the caller: the backend refuses (403)
// an edit of a rubric the teacher doesn't own, Jeddle templates included, and
// each caller words that failure for its own screen.
export const updateMarkingCriteria = async (
  markingCriteria,
  markingCriteriaId,
) => {
  return await postApi(
    baseUrl + '/teachers/markingCriteria/' + markingCriteriaId,
    markingCriteria,
    { throwOnError: true, suppressToast: true }
  );
};

export const deleteMarkingCriteria = async (markingCriteriaId) => {
  await deleteApi(baseUrl + '/teachers/markingCriteria/' + markingCriteriaId);
};

export const createNewFeedbackBank = async (newFeedbackBank) => {
  console.log('[copy comment bank] API request', {
    url: baseUrl + '/commentbanks',
    payload: newFeedbackBank,
  });
  const res = await postApi(baseUrl + '/commentbanks', newFeedbackBank);
  console.log('[copy comment bank] API response', res);
  return res;
};

export const createNewSmartAnnotation = async (
  smartAnnotation,
  smartAnnotationId,
) => {
  return await putApi(
    baseUrl + '/commentbanks/' + smartAnnotationId,
    smartAnnotation,
  );
};

export const updateSmartAnnotation = async (
  smartAnnotation,
  smartAnnotationId,
) => {
  return putApi(
    baseUrl + '/commentbanks/' + smartAnnotationId,
    smartAnnotation,
  );
};

export const deleteSmartAnnotation = async (smartAnnotationId) => {
  await deleteApi(baseUrl + '/commentbanks/' + smartAnnotationId);
};

export const getSmartAnnotations = async (filters = {}) =>
  await getApi(
    baseUrl + '/teachers/smartAnnotation' + buildQueryString(filters),
  );

export const getFeedbackBanks = async (filters = {}) => {
  if (!isLoggedIn()) {
    return [];
  }
  if (!isTeacher()) {
    const response = await getCommentBanksforStudents();
    return {
      _embedded: {
        commentbanks: response,
      },
    };
  }
  // Was: GET /commentbanks?projection=commentBanksProjection&size=10000
  // — that Spring-Data-REST endpoint 500s in prod (Java-17 module-system
  // reflection conflict serialising HttpMessageNotWritableException). Route
  // through /teachers/commentBanks (the actively-maintained endpoint) and
  // re-shape the response into the legacy { _embedded: { commentbanks } }
  // envelope so existing consumers (useCommentBanks, Header2, FeedbacksRoot,
  // CreateNewCommentBank) keep working without changing their access shape.
  const { commentBanks } = await getTeachersCommentBanks(filters);
  return {
    _embedded: {
      commentbanks: commentBanks,
    },
  };
};

export const getTeachersCommentBanks = async (filters = {}) => {
  if (!isLoggedIn() || !isTeacher()) return { commentBanks: [] };
  const response = await getApi(
    baseUrl + '/teachers/commentBanks' + buildQueryString(filters),
  );
  const list =
    response?.commentBanks ??
    response?._embedded?.commentbanks ??
    (Array.isArray(response) ? response : []);
  return { commentBanks: list };
};

export const getTeachersRubrics = async (filters = {}) => {
  if (!isLoggedIn() || !isTeacher()) return { rubrics: [] };
  const response = await getApi(
    baseUrl + '/teachers/markingCriteria' + buildQueryString(filters),
  );
  const list = Array.isArray(response) ? response : [];
  const rubrics = list.filter((item) => item?.type === 'RUBRICS');
  return { rubrics };
};

export const getAllSubmissions = async (id) =>
  await getApi(baseUrl + '/assignments/' + id + '/allSubmissions');

export const getCommentBank = async (id) => {
  const url = `${baseUrl}/commentbanks/${id}?projection=commentBanksProjection`;
  const token = getAuthToken(); // Retrieve the token if available

  const headers = new Headers();
  if (token) {
    headers.append('Authorization', `Bearer ${token}`);
  }

  try {
    const response = await fetch(url, {
      method: 'GET',
      credentials: 'include', // Ensure credentials are included for cookies
      headers: headers,
    });

    if (response.ok) {
      const isJson =
        response.headers.get('content-type')?.includes('application/json') ||
        response.headers.get('content-type')?.includes('application/hal+json');
      return isJson ? await response.json() : null; // Only parse JSON if the content type is correct
    } else {
      console.error('HTTP error:', response.status);
      return null; // Return null on any non-ok HTTP response
    }
  } catch (error) {
    console.error('Network or other error:', error);
    return null; // Return null on network errors or exceptions
  }
};

export const updateFeedbackBanks = async (updatedCommentBank, commentBankId) =>
  await putApi(baseUrl + '/commentbanks/' + commentBankId, updatedCommentBank);

const filterRubricsOnly = (items) => {
  if (items && typeof items === 'object' && !Array.isArray(items)) {
    return items?.type === 'RUBRICS' ? [items] : [];
  }
  if (!Array.isArray(items)) {
    return [];
  }
  return items.filter((item) => item?.type === 'RUBRICS');
};

export const getMarkingMethodology = async (id) => {
  if (!isLoggedIn()) {
    return [];
  }
  if (!isTeacher()) {
    return [];
  }
  const response = await getApi(baseUrl + '/teachers/markingCriterias/' + id);
  return filterRubricsOnly(response);
};
export const getAllMarkingCriteria = async (filters = {}) => {
  if (!isLoggedIn()) {
    return [];
  }
  if (!isTeacher()) {
    return [];
  }
  const response = await getApi(
    baseUrl + '/teachers/markingCriteria' + buildQueryString(filters),
  );
  return filterRubricsOnly(response);
};
export const getAllMarkingCriteriaWithPreferences = async () => {
  if (!isLoggedIn()) {
    return [];
  }
  if (!isTeacher()) {
    return [];
  }
  const response = await getApi(
    baseUrl + '/teachers/markingCriteria/preferences',
  );
  return response?.criteria || [];
};

export const updateMarkingCriteriaPreferences = async (operation, data) => {
  const requestData = {
    operation,
    ...data,
  };
  const response = await patchApi(
    baseUrl + '/teachers/markingCriteria/preferences',
    requestData,
  );
  return response;
};

export const getAllCommentBanksWithPreferences = async () => {
  if (!isLoggedIn()) {
    return [];
  }
  if (!isTeacher()) {
    return [];
  }
  const response = await getApi(baseUrl + '/teachers/commentBanks/preferences');
  return response.commentBanks;
};

export const updateCommentBankPreferences = async (operation, data) => {
  const requestData = {
    operation,
    ...data,
  };
  const response = await patchApi(
    baseUrl + '/teachers/commentBanks/preferences',
    requestData,
  );
  return response;
};

export const getCommentBankPreferences = async () => {
  if (!isLoggedIn()) {
    return [];
  }
  const response = await getApi(
    baseUrl + '/teachers/classes/commentBanks/preferences',
  );
  return response || [];
};

// Success Criteria API functions
export const getSuccessCriteria = async (id) => {
  if (!isLoggedIn()) {
    return [];
  }
  if (!isTeacher()) {
    return [];
  }
  if (id) {
    return await getApi(baseUrl + '/teachers/successCriteria/' + id);
  }
  return await getApi(baseUrl + '/teachers/successCriteria');
};

export const getAllSuccessCriteria = async (filters = {}) => {
  if (!isLoggedIn()) {
    return [];
  }
  if (!isTeacher()) {
    return [];
  }
  return await getApi(
    baseUrl + '/teachers/successCriteria' + buildQueryString(filters),
  );
};

// ── Course Materials (JED-967) ────────────────────────────────────────────────
// Humanities-only by design: the backend returns nothing for an English subject
// filter, so callers do not need their own gate.

export const getAllCourseMaterials = async (filters = {}) => {
  if (!isLoggedIn() || !isTeacher()) {
    return [];
  }
  return await getApi(
    baseUrl + '/teachers/courseMaterials' + buildQueryString(filters),
  );
};

// The same catalog, read by a student working on their own draft. Deliberately
// NOT gated on isTeacher(): that gate on the teacher endpoint is precisely why a
// student could never see curriculum in My Drafts.
export const getCourseMaterialsForStudentCatalog = async (filters = {}) => {
  if (!isLoggedIn()) {
    return [];
  }
  return await getApi(
    baseUrl + '/students/courseMaterials' + buildQueryString(filters),
  );
};

export const getCourseMaterial = async (id) => {
  if (!isLoggedIn() || !isTeacher()) {
    return null;
  }
  return await getApi(baseUrl + '/teachers/courseMaterials/' + id);
};

export const createCourseMaterial = async (courseMaterial) => {
  return await postApi(baseUrl + '/teachers/courseMaterials', courseMaterial);
};

export const updateCourseMaterial = async (id, courseMaterial) => {
  return await patchApi(
    baseUrl + '/teachers/courseMaterials/' + id,
    courseMaterial,
  );
};

export const deleteCourseMaterial = async (id) => {
  await deleteApi(baseUrl + '/teachers/courseMaterials/' + id);
};

// Creates the content a teacher attaches to ONE task via "Add course materials" —
// written text, or an already-uploaded file's URL for server-side text extraction.
// postApi resolves null on failure (see the two failure conventions), so callers
// must treat null as "couldn't save", not as success.
export const createTaskCourseMaterial = async ({ title, text, fileUrl, mimeType }) => {
  return await postApi(baseUrl + '/teachers/courseMaterials/task', {
    title,
    text,
    fileUrl,
    mimeType,
  });
};

// Parses an already-uploaded document into reviewable outcomes. postApi resolves
// null on failure (see the two failure conventions in service.js), so callers must
// treat null as "couldn't read that document" rather than an empty result.
export const parseCourseMaterialDocument = async (fileUrl, mimeType) => {
  return await postApi(baseUrl + '/teachers/courseMaterials/parse', {
    fileUrl,
    mimeType,
  });
};

export const getCourseMaterialsForStudents = async (assignmentId) => {
  if (!isLoggedIn()) {
    return [];
  }
  return await getApi(
    baseUrl +
      '/teachers/assignments/' +
      assignmentId +
      '/courseMaterialsForStudents',
  );
};

// Replaces the whole selection on a My Drafts submission; an empty array clears
// it. The server re-runs the humanities, visibility and per-question point-cap
// checks, and patchApi already toasts its 400 message before rejecting — so a
// caller only has to keep local state from drifting, not report the failure.
export const updateSubmissionCourseMaterials = async (
  submissionId,
  courseMaterials,
) => {
  return await patchApi(
    baseUrl + '/submissions/' + submissionId + '/courseMaterials',
    { courseMaterials: Array.isArray(courseMaterials) ? courseMaterials : [] },
  );
};

export const getAllSuccessCriteriaWithPreferences = async () => {
  if (!isLoggedIn()) {
    return [];
  }
  const response = await getApi(
    baseUrl + '/teachers/successCriteria/preferences',
  );
  if (Array.isArray(response)) {
    return response;
  }
  return response?.criteria || response || [];
};

export const createSuccessCriteria = async (successCriteria) => {
  console.log('[copy success criteria] API request', {
    url: baseUrl + '/teachers/successCriteria',
    payload: successCriteria,
  });
  const res = await postApi(
    baseUrl + '/teachers/successCriteria',
    successCriteria,
  );
  console.log('[copy success criteria] API response', res);
  return res;
};

export const updateSuccessCriteria = async (id, successCriteria) => {
  return await patchApi(
    baseUrl + '/teachers/successCriteria/' + id,
    successCriteria,
  );
};

export const deleteSuccessCriteria = async (id) => {
  await deleteApi(baseUrl + '/teachers/successCriteria/' + id);
};

export const updateSuccessCriteriaStatus = async (
  id,
  criteriaIndex,
  status,
) => {
  return await patchApi(
    baseUrl + '/teachers/successCriterias/' + id + '/criteriaStatus',
    {
      criteriaIndex,
      status,
    },
  );
};

export const updateSubmissionSuccessCriteriaStatus = async (
  submissionId,
  successCriteriaId,
  objectiveId,
  status,
  questionSerialNumber,
) => {
  return await patchApi(
    baseUrl +
      '/submissions/' +
      submissionId +
      '/successCriteria/' +
      successCriteriaId +
      '/criteriaStatus',
    {
      objectiveId,
      status,
      questionSerialNumber,
      ...(status === null ? { clearStatus: true } : {}),
    },
  );
};

// JED-1534: save several objective statuses of one success criteria tool in a
// single request. `updates` is [{ objectiveId, status }]; a null status clears it.
// No generic toast: the caller shows its own, and falls back on a 404 while an
// older backend without this route is still serving.
export const updateSubmissionSuccessCriteriaStatuses = async (
  submissionId,
  successCriteriaId,
  updates,
  questionSerialNumber,
) => {
  return await patchApi(
    baseUrl +
      '/submissions/' +
      submissionId +
      '/successCriteria/' +
      successCriteriaId +
      '/criteriaStatuses',
    {
      questionSerialNumber,
      updates: updates.map(({ objectiveId, status }) => ({
        objectiveId,
        status,
        ...(status === null ? { clearStatus: true } : {}),
      })),
    },
    { suppressToast: true },
  );
};

export const updateSubmissionShortAnswerStatus = async (
  submissionId,
  shortAnswersId,
  criteriaId,
  marks,
  questionSerialNumber,
) => {
  return await patchApi(
    baseUrl +
      '/submissions/' +
      submissionId +
      '/shortAnswers/' +
      shortAnswersId +
      '/marksStatus',
    {
      questionSerialNumber,
      criteriaId,
      marks,
    },
  );
};

export const updateSubmissionQuestionMark = async (
  submissionId,
  questionSerialNumber,
  awardedMarks,
) => {
  return await patchApi(
    baseUrl +
      '/submissions/' +
      submissionId +
      '/questions/' +
      questionSerialNumber +
      '/mark',
    {
      awardedMarks,
    },
  );
};

export const updateShortAnswerSuccessCriteriaStatus = async (
  submissionId,
  shortAnswersId,
  criteriaId,
  status,
) => {
  return await patchApi(
    baseUrl +
      '/submissions/' +
      submissionId +
      '/shortAnswers/' +
      shortAnswersId +
      '/successCriteriaStatus',
    {
      criteriaId,
      status,
    },
  );
};

export const updateSuccessCriteriaPreferences = async (operation, data) => {
  const requestData = {
    operation,
    ...data,
  };
  const response = await patchApi(
    baseUrl + '/teachers/successCriteria/preferences',
    requestData,
  );
  return response;
};

export const getSuccessCriteriaPreferences = async () => {
  if (!isLoggedIn()) {
    return [];
  }
  const response = await getApi(
    baseUrl + '/teachers/classes/successCriteria/preferences',
  );
  return response || [];
};

export const getCommentsForSubmission = async (params, options = {}) => {
  const submissionId =
    typeof params === 'object' ? params?.submissionId : params;
  if (!submissionId) {
    return [];
  }
  const version = typeof params === 'object' ? params?.version : null;
  const versionQuery =
    version !== undefined && version !== null ? `?version=${version}` : '';
  return await getApi(
    baseUrl + '/submissions/' + submissionId + '/comments' + versionQuery,
    options,
  );
};
export const getModelResponsesForClass = async (classId) =>
  await getApi(baseUrl + '/classes/' + classId + '/modelResponses');
export const getClassSettingForClass = async (classId) =>
  await getApi(baseUrl + '/classes/' + classId + '/settings');
export const updateClassSettingForClass = async (classId, classSetting) =>
  await putApi(baseUrl + '/classsettings/' + classId, classSetting);
export const getAdvancedMarkingTeacherSettings = async () =>
  await getApi(baseUrl + '/advanced-marking/teacher');
export const updateAdvancedMarkingTeacherSettings = async (controls) =>
  await putApi(baseUrl + '/advanced-marking/teacher', controls);
export const getAdvancedMarkingClassOverrides = async () =>
  await getApi(baseUrl + '/advanced-marking/teacher/classes');
export const updateAdvancedMarkingClassOverride = async (classId, controls) =>
  await putApi(
    baseUrl + '/advanced-marking/teacher/classes/' + classId,
    controls,
  );
export const deleteAdvancedMarkingClassOverride = async (classId) =>
  await deleteApi(baseUrl + '/advanced-marking/teacher/classes/' + classId);
export const getAdvancedMarkingStudentSettings = async () =>
  await getApi(baseUrl + '/advanced-marking/student');
export const updateAdvancedMarkingStudentSettings = async (feedbackAmount) =>
  await putApi(baseUrl + '/advanced-marking/student', { feedbackAmount });
export const getAdvancedMarkingEffectiveSettings = async ({
  assignmentId,
  classId,
}) =>
  await getApi(
    baseUrl +
      '/advanced-marking/effective?assignmentId=' +
      assignmentId +
      '&classId=' +
      classId,
  );
export const getAdvancedMarkingStudentClassEffectiveSettings = async (
  classId,
) =>
  await getApi(
    baseUrl + '/advanced-marking/student/classes/' + classId + '/effective',
  );
export const getStudentsForClass = async (classId) =>
  await getApi(baseUrl + '/classes/' + classId + '/students');
export const getTeachersForClass = async (classId) =>
  await getApi(baseUrl + '/classes/' + classId + '/teachers');

// Thundering-herd-safe single-flight cache. Was: only the resolved value
// was cached, so N concurrent callers (e.g. 82 TaskCards mounting at once)
// each saw the cache empty, each fired its own request, and each blocked
// the (already-overloaded) WP backend for 10-15s. Now: we also memoise the
// in-flight Promise so concurrent callers share one network round trip.
let classesResponseCache = null;
let classesResponsePromise = null;
export const getClassesResponse = async () => {
  if (classesResponseCache) return classesResponseCache;
  if (classesResponsePromise) return classesResponsePromise;
  classesResponsePromise = getApi(baseUrl + '/classes')
    .then((r) => {
      classesResponseCache = r;
      return r;
    })
    .finally(() => {
      classesResponsePromise = null;
    });
  return classesResponsePromise;
};

export const clearClassesResponseCache = () => {
  classesResponseCache = null;
  classesResponsePromise = null;
};

let subjectsResponseCache = null;
let subjectsResponsePromise = null;
export const getSubjectsFromSubjectsAPI = async () => {
  if (subjectsResponseCache) return subjectsResponseCache;
  if (subjectsResponsePromise) return subjectsResponsePromise;
  subjectsResponsePromise = getApi(baseUrl + '/api/v1/subjects')
    .then((r) => {
      subjectsResponseCache = r;
      return r;
    })
    .finally(() => {
      subjectsResponsePromise = null;
    });
  return subjectsResponsePromise;
};

export const clearSubjectsResponseCache = () => {
  subjectsResponseCache = null;
  subjectsResponsePromise = null;
};

let allSubjectsResponseCache = null;
let allSubjectsResponsePromise = null;

// Unfiltered subjects (every country/state) for the onboarding LOCATION picker — the
// country-filtered /api/v1/subjects would collapse a new (AU-defaulted) profile to AU-only,
// so a US user could never select United States/TX before saving their country.
export const getAllSubjectsFromAPI = async () => {
  if (allSubjectsResponseCache) return allSubjectsResponseCache;
  if (allSubjectsResponsePromise) return allSubjectsResponsePromise;
  allSubjectsResponsePromise = getApi(baseUrl + '/api/v1/subjects/all')
    .then((r) => {
      const list = Array.isArray(r) ? r : [];
      // Only cache a non-empty success; a transient failure/empty must not become a
      // session-sticky AU-only fallback (the original bug) — leave it uncached to retry.
      if (list.length) allSubjectsResponseCache = list;
      return list;
    })
    .catch(() => [])
    .finally(() => {
      allSubjectsResponsePromise = null;
    });
  return allSubjectsResponsePromise;
};

export const clearAllSubjectsResponseCache = () => {
  allSubjectsResponseCache = null;
  allSubjectsResponsePromise = null;
};

export const getSubjectsFromClasses = async () => {
  try {
    const res = await getSubjectsFromSubjectsAPI();
    if (Array.isArray(res)) return res;
    return [];
  } catch (e) {
    // Fallback to old /classes endpoint if new endpoint fails
    console.warn(
      'Failed to fetch from /api/v1/subjects, falling back to /classes',
      e,
    );
    const res = await getClassesResponse();
    if (Array.isArray(res?.subjects)) return res.subjects;
    const classes = Array.isArray(res) ? res : res?.classes || [];
    const byCode = new Map();
    classes.forEach((cls) => {
      (cls.subjects || []).forEach((s) => {
        if (s && (s.subjectCode || s.code)) {
          const code = s.subjectCode || s.code;
          if (!byCode.has(code)) byCode.set(code, { ...s });
        }
      });
    });
    return Array.from(byCode.values());
  }
};

const STATIC_CLASS_INSIGHTS_TASKS_NAMES_BY_ID = {
  18037: [
    { id: 101, name: 'Section I - 2024 HSC E...' },
    { id: 102, name: 'Animal farm' },
    { id: 103, name: 'Shakespeare Questions' },
  ],
};

const STATIC_CLASS_INSIGHTS_STUDENTS_BY_ID = {
  18037: [
    { id: 1, name: 'Anna Hermiston' },
    { id: 2, name: 'Angelina Kunde' },
    { id: 3, name: 'Chase Adams' },
    { id: 4, name: 'Harrison Connelly' },
    { id: 5, name: 'Hannah Koch' },
  ],
};

const STATIC_SUBMISSION_COUNT_BY_ID = {
  18037: 18037,
};

function collectTaskTypeStringsFromNestedSubjects(subjects) {
  const set = new Set();
  for (const s of subjects || []) {
    for (const tt of s.taskTypes || []) {
      if (tt && tt.taskType) set.add(tt.taskType);
    }
  }
  return Array.from(set);
}

function normalizeSubjectsForClasses(subjects) {
  if (!Array.isArray(subjects)) return [];
  return subjects.map((s) => ({
    ...s,
    code: s.subjectCode ?? s.code,
    name: s.subjectName ?? s.name,
  }));
}

function normalizeClassForInsights(cls) {
  const idStr = String(cls.id);
  const subjects = normalizeSubjectsForClasses(cls.subjects);
  const tasksNames =
    cls.tasksNames ?? STATIC_CLASS_INSIGHTS_TASKS_NAMES_BY_ID[idStr] ?? [];
  const students =
    cls.students ?? STATIC_CLASS_INSIGHTS_STUDENTS_BY_ID[idStr] ?? [];
  const taskTypesFromApi = cls.taskTypes;
  const taskTypes =
    Array.isArray(taskTypesFromApi) &&
    taskTypesFromApi.length &&
    typeof taskTypesFromApi[0] === 'string'
      ? taskTypesFromApi
      : collectTaskTypeStringsFromNestedSubjects(subjects);
  return {
    ...cls,
    name: cls.title ?? cls.name,
    subjects,
    tasksNames,
    students,
    taskTypes,
    submissionCount:
      cls.submissionCount ?? STATIC_SUBMISSION_COUNT_BY_ID[idStr] ?? 0,
  };
}

export const getClasses = async () => {
  const res = await getClassesResponse();
  const raw = Array.isArray(res) ? res : res?.classes || [];
  const normalized = raw.map(normalizeClassForInsights);
  return normalized.sort(
    (a, b) => (b.submissionCount || 0) - (a.submissionCount || 0),
  );
};

export const getActiveClasses = async () => {
  const { filterActiveClasses } = await import('./utils/archiveFilter');
  const all = await getClasses();
  return filterActiveClasses(all);
};
// Self-assessment normalization runs HERE, not only in the create-task
// transform: assignment writes also come from task duplication, TaskCard's
// dashboard publish, and full-document read-model writers (title edit,
// stimulus save, comment-bank change). This choke point covers them all —
// see utils/selfAssessmentNormalization.js for the invariant.
export const createAssignment = async (assignment, idempotencyKey) =>
  await postApi(
    baseUrl + '/assignments',
    normalizeAssignmentSelfAssessment(assignment),
    {
      headers: { 'Idempotency-Key': idempotencyKey || newIdempotencyKey() },
    },
  );
export const publishAssignment = async (id, update) =>
  await patchApi(baseUrl + '/assignments/' + id + '/publish', update);
export const schedulepublishAssignment = async (id, publishScheduledAt) =>
  await patchApi(
    baseUrl + '/assignments/' + id + '/schedulePublish',
    publishScheduledAt,
  );
export const updateSubject = async (id, subject) =>
  await patchApi(baseUrl + '/assignments/' + id + '/updateSubject', {
    subject: subject,
  });
export const closeAssignment = async (id) =>
  await patchApi(baseUrl + '/assignments/' + id + '/markClosed');

export const markTaskReopened = async (id) =>
  await patchApi(baseUrl + '/assignments/' + id + '/markReopened');

export const deleteAssignment = async (id, update) =>
  await patchApi(baseUrl + '/assignments/' + id + '/delete', update);

// ---------------------------------------------------------------------------
// Assignment submission batches — POST /assignments/{id}/submissions/batch
//
// Routes a document's pages to the students who wrote them, in one call. Replaces
// the /bulkDrafts/* staging layer: there is no intermediate draft resource any
// more, so pages land directly on real submissions and the wizard resumes from
// those instead of from local state.
//
// Assignment-scoped and JSON-only. Every file is already in storage by the time
// this is called (the drag-drop step uploads each one as it lands), so a batch is
// created from references, never from bytes.
// ---------------------------------------------------------------------------

// Rides the shared modifyData stack so an expired session still gets the 401
// teardown and the LMS-embedded relaunch notice. Toasts are suppressed because
// the caller renders its own copy from the error `code`, which survives the throw
// along with `details` (see apiError).
//
// Named for what it does rather than for the first endpoint that wanted it —
// draftPages reuses it, and a name that claims otherwise invites a change made
// "for the batch" to break them both silently.
const quietModifyResponse = async (url, options) =>
  modifyData(url, { suppressToast: true, ...options });

const quietModifyData = async (url, options) =>
  (await quietModifyResponse(url, options))?.data;

// Without a key, a retry after a network failure mints a second set of
// submissions. A repeated key within 24h replays the original 201 body instead.
export const newIdempotencyKey = () => uuidv4();

// A replayed key returns the ORIGINAL 201 body verbatim, so the payload alone
// cannot tell a replay from a first commit — and reporting a replay as a fresh
// commit leaves a teacher who double-submitted with a success and no explanation
// of why nothing changed. The status (201 created vs 200 replayed) and the
// conventional Idempotency-Replayed header are the only two signals there are.
const isIdempotentReplay = (response) =>
  response?.headers?.get?.('Idempotency-Replayed') === 'true' ||
  response?.status === 200;

const createdResult = (response) => ({
  ...(response?.data || {}),
  alreadySubmitted: isIdempotentReplay(response),
});

// ---------------------------------------------------------------------------
// Draft page snapshots — /assignments/{id}/draftPages
//
// The wizard's page list, saved as the teacher builds it. Submissions do not
// exist until the Organise commit, so between the Question step and that click
// nothing references the uploaded files and a refresh loses the lot — order,
// deletions, labels, manual typed pages and the routing. The files themselves
// were never at risk; the list of them was.
//
// A whole-array PUT, because the client already replaces the array wholesale on
// every mutation. `version` is optimistic concurrency: two tabs on one draft
// would otherwise clobber each other silently.
//
// Superseded by the submissions once they exist — the server drops the snapshot
// when the batch is created, and resume prefers submissions from then on.
// ---------------------------------------------------------------------------

const draftPagesUrl = (assignmentId) =>
  `${baseUrl}/assignments/${assignmentId}/draftPages`;

// The one place absent fields are defaulted, in both directions — callers that
// read a snapshot and callers that send one get the same shape from the same rule.
const toDraftPagesSnapshot = (data) => ({
  version: data?.version ?? 0,
  pages: data?.pages || [],
  studentAssignments: data?.studentAssignments || {},
  ...(data?.studentNames && Object.keys(data.studentNames).length ? { studentNames: data.studentNames } : {}),
});

/** A draft with no snapshot yet. Not an error: every new draft starts here. */
export const EMPTY_DRAFT_PAGES = Object.freeze(toDraftPagesSnapshot());

export const getDraftPages = async (assignmentId) => {
  if (!assignmentId) return EMPTY_DRAFT_PAGES;
  try {
    // getApi resolves the parsed body itself — unlike modifyData, which wraps it.
    const snapshot = await getApi(draftPagesUrl(assignmentId), {
      suppressToast: true,
    });
    return toDraftPagesSnapshot(snapshot);
  } catch (error) {
    if (error?.status === 404) return EMPTY_DRAFT_PAGES;
    throw error;
  }
};

/**
 * Throws on conflict rather than resolving — DRAFT_PAGES_CONFLICT is the one
 * failure a caller must branch on instead of retrying, since a blind retry is
 * how one tab overwrites another's work.
 */
export const saveDraftPages = async (assignmentId, snapshot) =>
  quietModifyData(draftPagesUrl(assignmentId), {
    method: 'PUT',
    body: JSON.stringify(toDraftPagesSnapshot(snapshot)),
  });

export const getCurrentSubmissionBatch = (assignmentId) =>
  quietModifyData(`${baseUrl}/assignments/${assignmentId}/submissions/batch`, { method: 'GET' });

export const createSubmissionBatch = async (
  assignmentId,
  payload,
  idempotencyKey,
) => {
  const url = `${baseUrl}/assignments/${assignmentId}/submissions/batch`;
  // A repeated save with the same key comes back as a replay (createdResult). A
  // 409 DRAFT_ALREADY_SUBMITTED is not one: it refuses THIS routing because an
  // earlier upload on the task is still open, and its submissionIds are that
  // upload's. Reading it as success paired those ids with the new students
  // (JED-1553: Review stuck on "Still converting"), so it is thrown like any
  // other refusal.
  return createdResult(
    await quietModifyResponse(url, {
      method: 'POST',
      headers: { 'Idempotency-Key': idempotencyKey || newIdempotencyKey() },
      body: JSON.stringify(payload),
    }),
  );
};

// ---------------------------------------------------------------------------
// Bulk feedback run — /assignments/{assignmentId}/jeddAIFeedback and
// /assignments/{assignmentId}/submissions/status
//
// One request asks for feedback for the whole assignment; one cheap read reports
// where every submission is. The fan-out used to live in the browser, so closing
// the tab mid-flight meant the remaining students never got feedback.
//
// getSubmissionsByAssignmentId stays and keeps its callers: it is the right
// endpoint for loading a list once, and the wrong one to poll — it resolves a
// DBRef per submission per tick. The status read is for polled reads only.
// ---------------------------------------------------------------------------

export const requestAssignmentFeedback = async (
  assignmentId,
  payload = {},
  idempotencyKey,
  { signal } = {},
) => {
  if (!assignmentId) throw new Error('This draft has not been created yet.');
  return quietModifyData(`${baseUrl}/assignments/${assignmentId}/jeddAIFeedback`, {
    method: 'POST',
    signal,
    throwOnError: true,
    headers: { 'Idempotency-Key': idempotencyKey || newIdempotencyKey() },
    body: JSON.stringify(payload),
  });
};

// Bulk upload on a task with more than one question — /submissions/{id}/questionSplit
//
// A batch creates ONE answer holding the whole paper. Before marking, the extracted
// text is split by question: propose says where each question seems to start, and
// apply stores what the teacher confirmed as one answer per question. Neither is
// called for a single-question task, and the server refuses both for one.
// ---------------------------------------------------------------------------

export const proposeQuestionSplit = async (submissionId) => {
  if (!submissionId) throw new Error('This response has not been created yet.');
  const response = await modifyData(
    `${baseUrl}/submissions/${submissionId}/questionSplit/propose`,
    { method: 'POST', suppressToast: true },
  );
  return response?.data;
};

export const applyQuestionSplit = async (submissionId, payload) => {
  if (!submissionId) throw new Error('This response has not been created yet.');
  const response = await modifyData(
    `${baseUrl}/submissions/${submissionId}/questionSplit`,
    { method: 'PUT', suppressToast: true, body: JSON.stringify(payload) },
  );
  return response?.data;
};

/**
 * Starts text extraction for the whole assignment, in one call.
 *
 * The fan-out this replaces lived in the browser: one POST per student, each with
 * its own attachment-generation header and 409-refetch retry, six at a time. A
 * 50-response batch spent that budget before the first page was read.
 *
 * Deliberately no Idempotency-Key, unlike requestAssignmentFeedback: the server
 * claims each answer by an attachment-generation CAS, so a replayed call finds it
 * already EXTRACTING and returns rather than enqueuing a second task. There is no
 * duplicated model spend to fence off, so there is no receipt to replay.
 *
 * @param {string} assignmentId
 * @param {Object} [options]
 * @param {string[]} [options.submissionIds] - a subset, for retrying the students
 *   that failed without re-extracting the class. Omit for all of them.
 */
export const startAssignmentExtraction = async (
  assignmentId,
  { submissionIds, signal } = {},
) => {
  if (!assignmentId) throw new Error('This draft has not been created yet.');
  return quietModifyData(
    `${baseUrl}/assignments/${assignmentId}/submissions/extractText`,
    {
      method: 'POST',
      signal,
      throwOnError: true,
      body: JSON.stringify(submissionIds?.length ? { submissionIds } : {}),
    },
  );
};

/**
 * `includeText` is opt-in: only the Converting step writes the extracted text onto
 * the wizard's pages. The feedback poll and the student switcher must not pay for
 * 200 students of it every two seconds.
 */
export const getAssignmentSubmissionsStatus = async (
  assignmentId,
  { includeText = false, signal } = {},
) => {
  if (!assignmentId) return null;
  const query = includeText ? '?includeText=true' : '';
  return await getApi(
    `${baseUrl}/assignments/${assignmentId}/submissions/status${query}`,
    { suppressToast: true, throwOnError: true, signal },
  );
};

/**
 * The `summary` of getAssignmentSubmissionsStatus for many assignments in one read, as
 * `{ [assignmentId]: summary }`. The task list draws its feedback progress bars from
 * this instead of one status read per card. The server takes at most 100 ids a call.
 *
 * A POST that only reads: 100 ids in a query string would share the server's 8KB
 * header budget with every cookie the browser sends along.
 */
export const getAssignmentFeedbackSummaries = async (assignmentIds, { signal } = {}) => {
  if (!assignmentIds?.length) return {};
  return await fetchApi(`${baseUrl}/assignments/submissionStatusSummaries`, {
    method: 'POST',
    body: JSON.stringify({ assignmentIds }),
    suppressToast: true,
    suppressRedirect: true,
    throwOnError: true,
    signal,
  });
};

export const extendDueAtAssignment = async (id, extendDueAt) =>
  await patchApi(baseUrl + '/assignments/' + id + '/extend', extendDueAt);
const putAssignment = async (id, assignment, options = {}) => {
  const result = await putApi(
    baseUrl + '/assignments/' + id,
    normalizeAssignmentSelfAssessment({ ...assignment,
      ...(assignmentTitleRevision(assignment) !== undefined && assignment.expectedTitleRevision === undefined
        ? { expectedTitleRevision: assignmentTitleRevision(assignment) } : {}),
    }),
    { ...options, throwOnError: true },
  );
  if (result?.id !== id) throw new Error('The assignment could not be saved. Please retry.');
  return result;
};
const writeAssignment = (id, operation, kind, options) => {
  const userId = getUserId();
  return runAssignmentWrite(userId, id, operation, () => getUserId() === userId, kind, options);
};
export const updateAssignment = (id, assignment, options = {}) =>
  writeAssignment(id, (signal) => putAssignment(id, assignment, { ...options, signal }), 'content', options);
export const updateAssignmentTitle = (id, title, expectedTitle, expectedTitleRevision) =>
  writeAssignment(id, (signal) => patchApi(baseUrl + '/assignments/' + id + '/title', { title, expectedTitle, ...(expectedTitleRevision !== undefined ? { expectedTitleRevision } : {}) }, { throwOnError: true, suppressToast: true, signal }), 'title');
// Rename a My Drafts row. Lands in the assignment's own displayTitle, never in the
// title the question editor writes, so the name and the question can differ. A null
// or blank name clears the rename and the row shows the question again. Shares the
// per-assignment write lane so it queues behind an in-flight question save.
export const updateAssignmentDisplayTitle = (id, displayTitle) =>
  writeAssignment(id, (signal) => patchApi(baseUrl + '/assignments/' + id + '/display-title', { displayTitle }, { throwOnError: true, signal }), 'displayTitle');
// Rebase only the requested fields after earlier title/stimulus/tool writes.
export const mutateAssignment = (id, change) => {
  const userId = getUserId();
  return writeAssignment(id, async (signal, throwIfExpired) => {
  const current = await getAssignmentById(id, { signal, throwOnError: true, suppressToast: true });
  throwIfExpired();
  if (getUserId() !== userId) throw new Error('The signed-in account changed. Reopen this draft.');
  if (current?.id !== id || assignmentTitleBaseline(current) === undefined) throw new Error('The saved assignment could not be loaded.');
  return putAssignment(id, { ...change(current), expectedTitle: assignmentTitleBaseline(current),
    ...(assignmentTitleRevision(current) !== undefined ? { expectedTitleRevision: assignmentTitleRevision(current) } : {}),
  }, { signal, suppressToast: true });
  });
};
export const flushAssignmentWrites = (id) => drainAssignmentWrites(getUserId(), id);
// Take your own copy of a task you don't own — the supported way to reuse a
// colleague's task now that saving over theirs is refused. The copy lands owned by
// the caller, as a DRAFT, carrying only classes the caller teaches.
// NOTE: postApi resolves to null on failure rather than rejecting, so callers must
// null-check rather than rely on try/catch.
export const cloneAssignment = async (id) =>
  await postApi(baseUrl + '/assignments/' + id + '/clone', {});
// A single answer is stored inline in the submission's Mongo document. Keep it well
// under Mongo's 16MB BSON document limit (other answers + version history share the
// same doc). Reject oversized payloads up front so the PATCH doesn't 500 on a loop
// (autosave retries every ~2s) and silently lose the student's work. The usual cause
// is a pasted base64 image — which the editor now also blocks at the paste itself.
export const MAX_ANSWER_CHARS = 5 * 1024 * 1024;

export const saveAnswer = async (
  submissionId,
  serialNumber,
  answer,
  options = {}
) => {
  const { attachmentGeneration = 0, ...requestOptions } = options;
  const serialized = JSON.stringify(answer ?? {});
  if (serialized.length > MAX_ANSWER_CHARS) {
    // Fixed toastId so repeated autosaves of the same oversized answer don't spam.
    toast(
      <Toast
        message={
          'This response is too large to save. Please remove some content.'
        }
      />,
      { toastId: 'answer-too-large' },
    );
    const err = new Error('ANSWER_TOO_LARGE');
    err.code = 'ANSWER_TOO_LARGE';
    throw err;
  }
  return await patchApi(
    baseUrl + '/submissions/' + submissionId + '/answers/' + serialNumber,
    answer,
    {
      ...requestOptions,
      headers: {
        ...requestOptions.headers,
        'X-Attachment-Generation': String(Number(attachmentGeneration) || 0),
      },
    }
  );
};

export const addToFavouriteList = async (feedbackId) =>
  await patchApi(
    baseUrl + '/feedbacks/modelResponses/' + feedbackId + '/addBookmark',
  );
export const removeFromFavouriteList = async (feedbackId) =>
  await patchApi(
    baseUrl + '/feedbacks/modelResponses/' + feedbackId + '/removeBookmark',
  );

// Staging-only QA model picker. Local storage is only the tester's selection,
// never the authority to use it: an entitlement derived from the authenticated
// profile must also be active in this page before any request can carry the
// override. The in-memory flag defaults closed on every page load.
export const MODEL_PICKER_STORAGE_KEY = 'jeddaiModelPickerProvider';
let modelPickerEntitled = false;

export const clearModelPickerOverride = () => {
  try {
    window.localStorage.removeItem(MODEL_PICKER_STORAGE_KEY);
  } catch (e) {
    // Storage can be unavailable in locked-down/private browsing contexts.
  }
};

export const setModelPickerEntitlement = (
  entitled,
  { clearOverride = true } = {},
) => {
  modelPickerEntitled = entitled === true;
  if (!modelPickerEntitled && clearOverride) clearModelPickerOverride();
};

export const getModelPickerConfig = async () =>
  await getApi(baseUrl + '/jeddai/models');

const modelPickerQuery = () => {
  if (!modelPickerEntitled) return '';
  try {
    const provider = window.localStorage.getItem(MODEL_PICKER_STORAGE_KEY);
    return provider ? '?modelProvider=' + encodeURIComponent(provider) : '';
  } catch (e) {
    return '';
  }
};

export const submitAssignment = async (submissionId) =>
  await patchApi(
    baseUrl + '/submissions/' + submissionId + '/submit' + modelPickerQuery(),
  );

// Establishes (or rejoins) the server-owned boundary for a class-work
// revision. The returned pendingRevisionStartedAt is the only clock the mobile
// editor trusts when separating this attempt's self-assessment evidence.
export const beginSubmissionRevision = async (submissionId) =>
  await patchApi(baseUrl + '/submissions/' + submissionId + '/beginRevision');

export const markSubmissionReviewed = async (submissionId) =>
  await patchApi(baseUrl + '/submissions/' + submissionId + '/reviewed');
export const markSubmissionRequestSubmission = async (submissionId) =>
  await patchApi(
    baseUrl + '/submissions/' + submissionId + '/requestResubmission',
  );
// A teacher sends a student's class work back to do again, with a new due date
// (ISO string). Works on JeddAI Instant tasks and reopens a closed task. Rejects
// on any refusal (the server's message is already toasted), so a caller never
// mistakes null for a reassign that happened.
export const reassignSubmission = async (submissionId, { dueAt }) =>
  await postApi(
    baseUrl + '/submissions/' + submissionId + '/reassign',
    { dueAt },
    { throwOnError: true },
  );
export const acceptFeedbackRequest = async (submissionId, options) =>
  await patchApi(
    baseUrl + '/submissions/' + submissionId + '/acceptFeedbackRequest',
    undefined,
    options,
  );
export const cancelFeedbackRequest = async (submissionId, options) =>
  await patchApi(
    baseUrl + '/submissions/' + submissionId + '/cancelFeedbackRequest',
    undefined,
    options,
  );
export const declineFeedbackRequest = async (submissionId) =>
  await patchApi(
    baseUrl + '/submissions/' + submissionId + '/declineFeedbackRequest',
  );

export const markSubmsissionClosed = async (submissionId) =>
  await patchApi(baseUrl + '/submissions/' + submissionId + '/closed');

// Recover the displayed failure into the workflow selected by the server.
export const resetFailedFeedback = async (submissionId, failureId) =>
  await patchApi(
    baseUrl + '/submissions/' + submissionId + '/resetFailedFeedback',
    failureId ? { failureId } : undefined,
  );

export const updateFeedbackRange = async (submissionId, commentId, range) =>
  await patchApi(
    baseUrl + '/submissions/' + submissionId + '/feedbacks/' + commentId,
    range,
  );
export const updateDocumentType = async (submissionId, documentType) =>
  await patchApi(
    baseUrl + '/submissions/' + submissionId + '/updateDocumentType',
    { documentType: documentType },
  );
export const updateSubmissionYear = async (submissionId, year, state) => {
  const body = {};
  if (year != null && year !== '') body.year = year;
  if (state != null && state !== '') body.state = state;
  if (Object.keys(body).length === 0) return;
  return patchApi(
    baseUrl + '/submissions/' + submissionId + '/updateYear',
    body,
  );
};

export const updateSubjectSettings = async (submissionId, settings) =>
  await patchApi(
    baseUrl + '/submissions/' + submissionId + '/subjectSettings',
    settings,
  );

export const updateAssignmentSubjectSettings = async (assignmentId, settings) =>
  await patchApi(
    baseUrl + '/assignments/' + assignmentId + '/subjectSettings',
    settings,
  );

export const publishModelResponse = async (feedbackId) =>
  await patchApi(
    baseUrl + '/feedbacks/modelResponses/' + feedbackId + '/publish',
  );

export const denyModelResponse = async (feedbackId) =>
  await patchApi(baseUrl + '/feedbacks/modelResponses/' + feedbackId + '/deny');

export const profileStateYear = async (stateYear) =>
  await patchApi(baseUrl + '/users/profile', stateYear);

export const neverShowSharedResourcePopup = async () =>
  await patchApi(
    baseUrl + `/users/profile/never-show-shared-resource-popup`,
    {},
  );

export const createRequestFeddbackType = async (
  submissionId,
  requestFeddbackType,
) =>
  await patchApi(
    baseUrl + '/submissions/' + submissionId + '/requestFeedback',
    requestFeddbackType,
  );
function logoutLocal() {
  isLoggedOut = true;
  localStorage.clear();
  deleteProfileCookies();
  localStorage.removeItem('jwtToken');
  localStorage.removeItem('classes');
  // Drop the sticky embedded markers so a fresh top-level login in this tab is not
  // treated as still-embedded (which would suppress the IdP redirect / alter 401
  // handling). No-op when nothing was stored.
  clearEmbeddedSession();
  clearLaunchContext();
}

export const exchangeCodeForToken = (code) =>
  fetchData(baseUrl + `/users/exchange/${code}`);

// LTI 1.3 launch handoff (PLAN §7.1, §8.3): the backend mints a one-time lti_code
// and the SPA exchanges it for the app session. Mirrors exchangeCodeForToken — same
// fetchData path, same response shape ({ token, userId, name, role, permissions, ... }).
export const exchangeLtiCodeForToken = (code) =>
  fetchData(baseUrl + `/lti/exchange/${code}`);

// LTI 1.3 Deep Linking authoring return (PR-11 / PLAN §7.2, §8.1).
// Implementation lives in service/lti.api.js; re-exported here so components can
// import it from the service barrel like every other call.
export { postDeepLinkReturn } from './service/lti.api';

export const getShortcuts = () => {
  const shortcuts = [
    { text: 'Use of evidence' },
    { text: 'Selection of evidence' },
    { text: 'Contextualise evidence' },
    { text: 'Incorrect syntax' },
    { text: 'Overcomplicated syntax' },
    { text: 'Paragraph cohesion' },
    { text: 'Use of techniques' },
    { text: 'Lack of specificity' },
    { text: 'Reference to the question' },
    { text: 'Analytical tone' },
    { text: 'Concision and precision' },
    { text: 'Use of Context' },
    { text: 'Setting' },
    { text: 'Character' },
    { text: 'Plot' },
    { text: 'Syntax for creative effect' },
  ];
  return shortcuts;
};

export const getFocusAreas = async () => {
  return await getApi(baseUrl + '/feedbacks/focusAreas');
};

export const addFocusArea = async (focusArea) =>
  await postApi(baseUrl + '/feedbacks/focusAreas', focusArea);

export const getAllColors = () => {
  return [
    '#F0C8C8',
    '#F6DEDE',
    '#F8DBB9',
    '#FBECDA',
    '#D0E4C4',
    '#E5F0DF',
    '#C0DFE4',
    '#D9ECEF',
    '#D7D1EA',
    '#E7E3F2',
    '#B5CFED',
    '#D3E2F4',
    '#F5E9D6',
    '#CCE7E1',
    '#F4E5F6',
    '#D1D1EB',
  ];
};

export const getNewCriteria = (criteriaId) => {
  return {
    id: { criteriaId },
    title: '',
    levels: [
      {
        id: `${Date.now()}_0`,
        name: '',
        description: '',
      },
      {
        id: `${Date.now()}_1`,
        name: '',
        description: '',
      },
      {
        id: `${Date.now()}_2`,
        name: '',
        description: '',
      },
    ],
  };
};

export const getDefaultCriteria = () => {
  const criteria = getNewCriteria(0);
  return {
    title: 'Untitled Rubric',
    criterias: [criteria],
  };
};
export const getPortfolio = async () =>
  await getApi(baseUrl + '/students/portfolio');

export const addDocumentToPortfolioWithDetails = async (documentDetails) =>
  await postApi(baseUrl + '/students/portfolio/documents', documentDetails);

export const createDraftWithSettings = async ({
  subjectName,
  subjectCode,
  taskType,
  state,
  year,
  responseType,
  totalMarks,
  title = 'Untitled Question',
}) => {
  const payload = {
    title: title?.trim() || 'Untitled Question',
    documentType: taskType,
  };
  const trimmedSubjectCode = String(subjectCode || '').trim();
  if (trimmedSubjectCode) {
    payload.subject = {
      subjectCode: trimmedSubjectCode,
      subjectName: String(subjectName || '').trim(),
    };
  }
  if (year != null && String(year).trim()) payload.year = String(year).trim();
  if (state != null && String(state).trim())
    payload.state = String(state).trim();
  if (responseType != null && String(responseType).trim())
    payload.responseType = String(responseType).trim();
  if (totalMarks != null && String(totalMarks).trim())
    payload.totalMarks = Number(totalMarks);

  const created = await addDocumentToPortfolioWithDetails(payload);
  if (created?.id) {
    trackProductEvent('draft_started', { draft_id: created.id, from: 'new' });
  }
  return created;
};

export const askJeddAI = async (
  submissionId,
  cleanAnswer,
  markingCriteriaId,
  feedbackBankId,
  successCriteriaIds,
  shortAnswerId,
  questionMarks,
  totalMarks,
  // Upstream shape: a single object carrying every advanced marking field.
  // The caller (FeedbacksRoot.jeddAI) resolves stored settings and folds any
  // per-draft override into this object before calling, so this function just
  // serialises what it is handed.
  advancedMarkingSettings,
  documentReviewContext,
) => {
  const url = baseUrl + '/submissions/' + submissionId + '/jeddAIFeedback' + modelPickerQuery();

  // Merge all advanced marking settings into payload
  const payload = {
    state: getLocalStorage('state'),
    year: getLocalStorage('year'),
    cleanAnswer: cleanAnswer,
    markingCriteriaId: markingCriteriaId,
    feedbackBankId: feedbackBankId,
    successCriteriaIds: successCriteriaIds || [],
    shortAnswerId: shortAnswerId,
    questionMarks: questionMarks || [],
    totalMarks: totalMarks != null ? Number(totalMarks) : undefined,
    // Send all advanced marking settings to backend
    rubricDifficulty: advancedMarkingSettings?.rubricDifficulty || undefined,
    specificCommentQuantity: advancedMarkingSettings?.specificCommentQuantity || undefined,
    aiReplyDetail: advancedMarkingSettings?.aiReplyDetail || undefined,
    sophisticationEnabled: advancedMarkingSettings?.sophisticationEnabled !== undefined
      ? advancedMarkingSettings.sophisticationEnabled
      : undefined,
    summaryLength: advancedMarkingSettings?.summaryLength || undefined,
    // Student channel: a single FeedbackAmount name the backend maps onto the
    // amount fields itself (students never send the five fields above).
    feedbackAmount: advancedMarkingSettings?.feedbackAmount || undefined,
    feedbackFocus: advancedMarkingSettings?.feedbackFocus?.trim() || undefined,
    // My Drafts only (documentRequestChoices): "Select my own" marks with just
    // the tools above (JED-907), and the advanced fields are this draft's
    // Advanced-tab override, remembered for its next popup (JED-1401).
    toolSelection: advancedMarkingSettings?.toolSelection || undefined,
    perDraftAdvancedOverride:
      advancedMarkingSettings?.perDraftAdvancedOverride === true ? true : undefined,
  };

  const { cleanAnswer: ignoredClientText, questionMarks: ignoredClientQuestionMarks, ...settings } = payload;
  Object.assign(payload, documentReviewIntent(documentReviewContext?.attempt, documentReviewContext?.submission, { ...settings, providerQuery: modelPickerQuery() }));
  const dispatchedAttempt = documentReviewContext?.attempt?.current;
  if (modelPickerQuery() === '?modelProvider=jev' && payload.operationId) {
    documentReviewContext?.onJevStreamStart?.(payload.operationId);
  }
  // A caller that can explain a credit refusal (onCreditRefusal) shows its own
  // upgrade prompt instead of the raw backend message.
  const onCreditRefusal = documentReviewContext?.onCreditRefusal;
  const response = await postApi(url, payload, {
    includeMessages: true,
    quietErrorCodes: onCreditRefusal ? [INSUFFICIENT_CREDITS] : undefined,
  });
  const result = response?.data;
  const ownsAttempt = documentReviewContext?.attempt && documentReviewContext.attempt.current === dispatchedAttempt;
  if (result && ownsAttempt) documentReviewContext.attempt.current = null;
  // A 400 is a definite refusal: nothing was admitted under this operation, so
  // the next attempt starts a new one. Replaying a refused operation while the
  // backend is still settling its retired reservation answers 409 instead of
  // the refusal.
  if (response?.status === 400) {
    if (ownsAttempt) documentReviewContext.attempt.current = null;
    documentReviewContext?.onRequestRefused?.();
  }
  if (response?.errorCode === INSUFFICIENT_CREDITS)
    onCreditRefusal?.(response.errorMessage);
  return result;
};

export const streamJevFeedback = async (submissionId, operationId, onEvent, signal) => {
  const token = getAuthToken();
  const query = operationId ? '?operationId=' + encodeURIComponent(operationId) : '';
  const response = await fetch(baseUrl + '/submissions/' + encodeURIComponent(submissionId) + '/jevFeedback/stream' + query, {
    method: 'GET', credentials: 'include', signal,
    headers: { Accept: 'text/event-stream', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
  return readFeedbackEvents(response, onEvent, signal);
};

export const retryDocumentFeedbackDelivery = (submission, options = {}) => startRunWithDeadline(signal => postApi(
  baseUrl + '/submissions/' + submission.id + '/jeddAIFeedback/retryDelivery',
  { editToken: submission.editToken, expectedCurrentVersion: submission.currentVersion },
  { throwOnError: true, suppressToast: true, signal },
), options);

export const getFallbackShortAnswers = async () =>
  getApi(baseUrl + '/jeddai/tools/fallbackShortAnswers');

// Cloud Run's HTTP/1 front end rejects requests above 32 MiB (413 before the
// request reaches Spring). The cutoff is the cap itself — NOT the cap minus
// multipart overhead — so every file size that can work on the legacy endpoint
// today still tries it first (undershooting would permanently reroute working
// sizes); the boundary sliver where multipart framing tips the request over
// simply 413s and falls through to the direct-to-GCS backup.
const LEGACY_UPLOAD_MAX_BYTES = 32 * 1024 * 1024;

// Mirrors fetchData(): in cookie-auth mode getAuthToken() is null and the JWT
// rides on the session cookie instead. Both /uploads/** endpoints are
// authenticated (unlike permitAll /files/upload), so both transports must work.
const authedJsonPost = (body, signal) => {
  const token = getAuthToken();
  return {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    },
    body: JSON.stringify(body),
    signal,
  };
};

const signRequestBody = (file) => ({
  fileName: file.name,
  // Some browsers hand over File objects with an empty type; the backend derives
  // it from the extension (single source — the signed PUT's Content-Type comes
  // back in requiredHeaders either way).
  contentType: file.type || '',
  fileSizeBytes: file.size,
});

const okOrThrow = (response, what) => {
  if (!response.ok) throw new Error(`${what}: HTTP ${response.status}`);
  return response;
};

const completeRequestBody = (file, accessUrl) => ({
  accessUrl,
  fileName: file.name,
  contentType: file.type || '',
  sizeBytes: file.size,
});

const signHandwrittenUpload = async (file, signal) => {
  const url = `${baseUrl}/uploads/handwritten/sign`;
  const response = await fetch(url, authedJsonPost(signRequestBody(file), signal));
  return okOrThrow(response, 'Sign request failed').json();
};

/**
 * Records the finished upload in the uploadedFiles registry.
 *
 * Only POST /files/upload writes that registry today, and
 * SubmissionBatchValidator.findFile 404s any fileUrl missing from it. So a file
 * that takes the direct path reaches storage, shows up in the wizard, and then
 * fails the Organise commit with FILE_NOT_FOUND. Registering here is what makes
 * the direct path usable for bulk upload at all.
 */
const registerHandwrittenUpload = async (file, accessUrl, signal) => {
  const url = `${baseUrl}/uploads/handwritten/complete`;
  const response = await fetch(url, authedJsonPost(completeRequestBody(file, accessUrl), signal));
  okOrThrow(response, 'Upload registration failed');
  return accessUrl;
};

// Direct browser→GCS upload: bypasses Cloud Run's 32 MiB request cap entirely.
// requiredHeaders come from the backend and are part of the URL's V4 signature
// (Content-Type) or enforced by GCS (x-goog-content-length-range) — echo them
// verbatim or GCS rejects the PUT.
//
// fetch rejects with a TypeError when storage never answers: DNS failure, a school
// web filter that blocks storage.googleapis.com, a block page without CORS headers,
// or a dropped connection. That error is marked storageUnreachable so the caller
// can retry through the API, which those networks still reach. An HTTP answer from
// storage (403, 412, ...) is not marked: storage was reached and refused the file.
const putToSignedUrl = async (uploadUrl, uploadedFile, requiredHeaders, signal) => {
  let response;
  try {
    response = await fetch(uploadUrl, {
      method: 'PUT',
      headers: requiredHeaders || {},
      body: uploadedFile,
      signal,
    });
  } catch (error) {
    if (!signal?.aborted && error instanceof TypeError) error.storageUnreachable = true;
    throw error;
  }
  okOrThrow(response, 'Direct upload failed');
};

// The url is returned only once the blob is BOTH stored and registered — an
// unregistered file looks uploaded and fails the commit 40 pages later.
const uploadFileDirectToStorage = async (uploadedFile, signal) => {
  const { uploadUrl, accessUrl, requiredHeaders } =
    await signHandwrittenUpload(uploadedFile, signal);
  await putToSignedUrl(uploadUrl, uploadedFile, requiredHeaders, signal);
  return registerHandwrittenUpload(uploadedFile, accessUrl, signal);
};

const uploadFileToLegacyEndpoint = async (uploadedFile, signal) => {
  const formData = new FormData();
  formData.append('file', uploadedFile);

  const token = getAuthToken();
  const response = await fetch(`${baseUrl}/files/upload`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      ...(token && { Authorization: `Bearer ${token}` }),
    },
    body: formData,
    signal,
  });

  const responseText = await response.text();

  if (!response.ok) {
    throw Object.assign(new Error(responseText), { status: response.status });
  }
  if (!isUploadedFileLink(responseText)) {
    datadogRum?.addAction?.('handwritten_upload_reply_not_a_link', {
      replyLength: responseText.length,
      contentType: response.headers?.get?.('content-type') || '',
      contentEncoding: response.headers?.get?.('content-encoding') || '',
    });
    throw new Error('Upload reply was not a file link');
  }
  return responseText;
};

// The reply to a good upload is always an absolute http(s) link: {api}/files/handwritten/… from
// /files/upload, a signed https://storage.googleapis.com/… link from /stimulus/upload (or
// {api}/files/gridfs/… on local fake storage). A browser that receives the reply still compressed
// (its Content-Encoding header dropped on the way) hands back binary junk instead, which used to
// be saved as the student's page (WP 74235, 2026-10) or the task's stimulus image. Throwing here
// sends handwritten images and PDFs to the direct-to-storage path, fails a Word upload cleanly,
// and fails a stimulus upload so the editor shows its upload error.
const isUploadedFileLink = (text) =>
  /^https?:\/\/[^\s\u0000-\u001f\u007f-\u009f\ufffd]+$/i.test(text);

// The converter admits two requests per backend instance. Share this queue
// between the bulk wizard and individual response uploads so a multi-file drop
// does not overwhelm it. Other browser sessions can still make it busy.
const MAX_ACTIVE_WORD_UPLOADS = 2;
const MAX_QUEUED_WORD_UPLOADS = 100;
const WORD_UPLOAD_RETRY_DELAYS = [500, 1500, 3500];
const wordUploadQueue = [];
let activeWordUploads = 0;

const wordUploadAborted = () => Object.assign(new Error('Upload cancelled'), { name: 'AbortError' });

const releaseWordUploadSlot = () => {
  const next = wordUploadQueue.shift();
  if (next) next.start();
  else activeWordUploads -= 1;
};

const acquireWordUploadSlot = (signal) => {
  if (signal?.aborted) throw wordUploadAborted();
  if (activeWordUploads < MAX_ACTIVE_WORD_UPLOADS) {
    activeWordUploads += 1;
    return releaseWordUploadSlot;
  }
  if (wordUploadQueue.length >= MAX_QUEUED_WORD_UPLOADS) {
    throw new Error('Too many Word documents are waiting to upload. Retry this file after the current uploads finish.');
  }
  return new Promise((resolve, reject) => {
    const waiting = {
      start: () => {
        signal?.removeEventListener('abort', onAbort);
        resolve(releaseWordUploadSlot);
      },
    };
    const onAbort = () => {
      const index = wordUploadQueue.indexOf(waiting);
      if (index >= 0) wordUploadQueue.splice(index, 1);
      reject(wordUploadAborted());
    };
    signal?.addEventListener('abort', onAbort, { once: true });
    wordUploadQueue.push(waiting);
  });
};

const waitForUploadRetry = (delay, signal) => new Promise((resolve, reject) => {
  if (signal?.aborted) {
    reject(wordUploadAborted());
    return;
  }
  const timer = setTimeout(() => {
    signal?.removeEventListener('abort', onAbort);
    resolve();
  }, delay);
  const onAbort = () => {
    clearTimeout(timer);
    reject(wordUploadAborted());
  };
  signal?.addEventListener('abort', onAbort, { once: true });
});

const uploadWordFileToServer = async (file, signal) => {
  let release;
  try {
    if (signal?.aborted || file.size > LEGACY_UPLOAD_MAX_BYTES) return null;
    release = await acquireWordUploadSlot(signal);
    for (let attempt = 0; attempt <= WORD_UPLOAD_RETRY_DELAYS.length; attempt += 1) {
      if (signal?.aborted) return null;
      try {
        return await uploadFileToLegacyEndpoint(file, signal);
      } catch (error) {
        if (error.status !== 503 || attempt === WORD_UPLOAD_RETRY_DELAYS.length) throw error;
        await waitForUploadRetry(WORD_UPLOAD_RETRY_DELAYS[attempt], signal);
      }
    }
  } catch (error) {
    if (!signal?.aborted) {
      console.error('Word upload failed:', error);
      datadogRum?.addAction?.('handwritten_word_upload_failed', {
        reason: String(error?.message || error),
        fileSizeBytes: file.size,
      });
    }
    return null;
  } finally {
    release?.();
  }
};

// Returns the stored file URL (same {api}/files/handwritten/{blob} shape as
// always) or null on failure — callers (HandWritten.jsx) rely on that contract.
//
// Word uploads use the bounded conversion queue and never send their original
// bytes directly to storage. PDF/image path choice remains:
// - Files at or under Cloud Run's 32 MiB request cap take EXACTLY the path they
//   always have — POST /files/upload, including its server-side PDF compression
//   — with direct-to-GCS as a new fallback if that fails. Nothing that works
//   today changes path or behavior.
// - Larger files (which today 413 at Cloud Run's front end before reaching the
//   backend) go direct-to-GCS via a signed PUT; there is no legacy fallback for
//   them because the legacy endpoint structurally cannot accept them.
// - When the backup's storage PUT gets no answer at all (some school filters block
//   storage.googleapis.com but not our API), a file the legacy endpoint can accept
//   gets one more legacy attempt, provided its first failure could be transient.
const uploadFileToServerRaw = async (uploadedFile, { signal } = {}) => {
  if (/\.docx$/i.test(uploadedFile?.name || '')) {
    return uploadWordFileToServer(uploadedFile, signal);
  }
  let legacyError = null;
  if (uploadedFile?.size <= LEGACY_UPLOAD_MAX_BYTES) {
    try {
      return await uploadFileToLegacyEndpoint(uploadedFile, signal);
    } catch (error) {
      if (signal?.aborted) return null;
      if (error instanceof TypeError) {
        console.error('Network error:', error);
      } else {
        console.error('Server error:', error);
      }
      legacyError = error;
      // fall through to the direct-to-GCS backup below
    }
  }

  try {
    return await uploadFileDirectToStorage(uploadedFile, signal);
  } catch (error) {
    if (signal?.aborted) return null;
    console.error('Direct-to-storage upload failed:', error);
    datadogRum?.addAction?.('handwritten_direct_upload_failed', {
      reason: String(error?.message || error),
      fileSizeBytes: uploadedFile?.size,
      contentType: uploadedFile?.type || '',
      storageUnreachable: error?.storageUnreachable === true,
    });
    if (!canRetryThroughApi(error, uploadedFile, legacyError)) return null;
    return retryThroughApi(uploadedFile, signal);
  }
};

// Only a validated returned link proves registration; filenames and links never
// enter telemetry. A null result is failure unless the caller cancelled it.
export const uploadFileToServer = async (uploadedFile, options = {}) => {
  const finish = startJourneyOperation('handwritten_upload');
  try {
    const result = await uploadFileToServerRaw(uploadedFile, options);
    finish(result ? 'success' : options.signal?.aborted ? 'unconfirmed' : 'failure');
    return result;
  } catch (error) {
    finish('failure', error?.status);
    throw error;
  }
};

const API_RETRY_DELAY_MS = 1000;

// A second legacy attempt only helps when storage never answered (so the direct path
// cannot work from this network), the API can take the file's size, and the first
// legacy failure could have been transient: no answer, a 5xx or a 429. A 4xx or a
// reply that was not a link would only repeat.
const canRetryThroughApi = (directError, file, legacyError) =>
  directError?.storageUnreachable === true &&
  file?.size <= LEGACY_UPLOAD_MAX_BYTES &&
  (legacyError instanceof TypeError || legacyError?.status >= 500 || legacyError?.status === 429);

const retryThroughApi = async (file, signal) => {
  try {
    await waitForUploadRetry(API_RETRY_DELAY_MS, signal);
    const fileUrl = await uploadFileToLegacyEndpoint(file, signal);
    datadogRum?.addAction?.('handwritten_upload_api_retry', {
      outcome: 'succeeded',
      fileSizeBytes: file.size,
    });
    return fileUrl;
  } catch (error) {
    if (signal?.aborted) return null;
    console.error('Upload retry through the API failed:', error);
    datadogRum?.addAction?.('handwritten_upload_api_retry', {
      outcome: 'failed',
      reason: String(error?.message || error),
      fileSizeBytes: file.size,
    });
    return null;
  }
};

const uploadStimulusFileToServerRaw = async (
  uploadedFile,
  { signal } = {},
) => {
  const formData = new FormData();
  formData.append('file', uploadedFile);

  const token = getAuthToken();
  try {
    const response = await fetch(`${baseUrl}/stimulus/upload`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
      signal,
    });

    const responseText = await response.text();

    if (!response.ok) {
      throw new Error(responseText);
    }
    if (!isUploadedFileLink(responseText)) {
      datadogRum?.addAction?.('stimulus_upload_reply_not_a_link', {
        replyLength: responseText.length,
        contentType: response.headers?.get?.('content-type') || '',
        contentEncoding: response.headers?.get?.('content-encoding') || '',
      });
      throw new Error('Stimulus upload reply was not a file link');
    }
    return responseText;
  } catch (error) {
    if (signal?.aborted) return null;
    if (error instanceof TypeError) {
      console.error('Network error:', error);
    } else {
      console.error('Server error:', error);
    }
    return null;
  }
};

export const uploadStimulusFileToServer = async (uploadedFile, options = {}) => {
  const finish = startJourneyOperation('stimulus_upload');
  try {
    const result = await uploadStimulusFileToServerRaw(uploadedFile, options);
    finish(result ? 'success' : options.signal?.aborted ? 'unconfirmed' : 'failure');
    return result;
  } catch (error) {
    finish('failure', error?.status);
    throw error;
  }
};

export const updateHandWrittenDocumentById = async (
  submissionId,
  serialNumber,
  documentUrls,
  options = {}
) => {
  const { attachmentGeneration = 0, ...requestOptions } = options;
  return await patchApi(
    baseUrl +
      '/submissions/' +
      submissionId +
      '/answers/' +
      serialNumber +
      '/files',
    documentUrls,
    {
      ...requestOptions,
      headers: {
        ...requestOptions.headers,
        'X-Attachment-Generation': String(Number(attachmentGeneration) || 0),
      },
    }
  );
};

export const extractText = async (id, serialNumber, options = {}) => {
  const { attachmentGeneration = 0, ...requestOptions } = options;
  return await patchApi(
    baseUrl +
      '/submissions/' +
      id +
      '/answers/' +
      serialNumber +
      '/extractText',
    undefined,
    {
      ...requestOptions,
      headers: {
        ...requestOptions.headers,
        'X-Attachment-Generation': String(Number(attachmentGeneration) || 0),
      },
    }
  );
};

// Async variant: enqueues extraction and returns immediately (answer.extractionStatus = EXTRACTING).
// The UI then polls getSubmissionById until the answer reaches EXTRACTED / FAILED. This keeps slow
// handwritten extractions off the synchronous request path (which the edge cuts off at ~125s).
export const extractTextAsync = async (id, serialNumber, options = {}) => {
  const { attachmentGeneration = 0, ...requestOptions } = options;
  // Unlike generic postApi, preserve an HTTP 409 for the answer save lane.
  // A generation conflict is authoritative evidence that another attachment
  // write won; flattening it to null would make callers retry extraction
  // against stale files instead of refetching and blocking the lifecycle.
  const response = await modifyData(
    baseUrl +
      '/submissions/' +
      id +
      '/answers/' +
      serialNumber +
      '/extractText/async',
    {
      method: 'POST',
      body: JSON.stringify(undefined),
      ...requestOptions,
      headers: {
        ...requestOptions.headers,
        'X-Attachment-Generation': String(Number(attachmentGeneration) || 0),
      },
    }
  );
  return response?.data;
};

export const getProfile = async () => {
  // Gate on the auth token, NOT isLoggedIn() (which checks localStorage.userId).
  // On an LTI launch the exchange sets the JWT first and userId only AFTER this
  // call returns the profile — gating on userId would make getProfile return null
  // forever (chicken-and-egg), leaving the app stuck "logged out".
  if (!getAuthToken()) {
    return null;
  }

  const token = getAuthToken();
  let identityGeneration = null;
  try {
    if (isStagingRumHost()) identityGeneration = getStagingIdentityGeneration();
  } catch {
    // Diagnostics must not interrupt profile loading.
  }
  const profile = await getApi(baseUrl + '/users/profile');
  try {
    if (
      identityGeneration !== null &&
      isCurrentStagingIdentityGeneration(identityGeneration) &&
      token === getAuthToken() &&
      profile?.userId
    ) setStagingRumUser(profile.userId);
  } catch {
    // Diagnostics must not interrupt profile loading.
  }
  return profile;
};

export const updateLastOnboardingTime = async () =>
  await patchApi(baseUrl + '/users/shownOnboarding');

export const markOnboardingCompleted = async () =>
  await patchApi(baseUrl + '/users/completedOnboarding');

export const disableOnboarding = async () =>
  await patchApi(baseUrl + '/users/disableOnboarding');

export const reenableOnboarding = async () =>
  await patchApi(baseUrl + '/users/reenableOnboarding');

export const getPreferenceMarkingCriteria = async () => {
  if (!isLoggedIn()) {
    return [];
  }
  return await getApi(baseUrl + '/teachers/classes/preferences');
};

export const getMarkingCriteriasforStudents = async () => {
  if (!isLoggedIn()) {
    return [];
  }
  const response = await getApi(baseUrl + '/students/markingCriteria');
  return filterRubricsOnly(response);
};

export const getCommentBanksforStudents = async () => {
  if (!isLoggedIn()) {
    return [];
  }
  return await getApi(baseUrl + '/students/commentBanks');
};

export const getSuccessCriteriaForStudents = async () => {
  if (!isLoggedIn()) {
    return [];
  }
  return await getApi(baseUrl + '/students/successCriteria');
};

export const getShortAnswersForStudents = async () => {
  if (!isLoggedIn()) {
    return [];
  }
  return await getApi(baseUrl + '/students/shortAnswers');
};

export const shareSubmissionDoc = async (
  id,
  sharedWith = [],
  shareType = 'ANYONE_WITH_LINK',
) => {
  const sharedSubmission = {
    sharedWith: shareType,
    sharedWithUserIds: sharedWith,
  };

  return await patchApi(
    baseUrl + '/submissions/' + id + '/share',
    sharedSubmission,
  );
};

export const fetchSharedSubmission = async (id) => {
  return await getApi(baseUrl + '/submissions/' + id + '/share');
};

export const updateSettings = async (
  markingCriteriaId,
  commentBankId,
  shortAnswerId,
  successCriteriaIds,
  options = {},
) => {
  const preferences = {
    lastUsedMarkingCriteriaId: markingCriteriaId,
    lastUsedCommentBanksId: commentBankId,
    ...(shortAnswerId && { lastUsedShortAnswerId: shortAnswerId }),
    ...(Array.isArray(successCriteriaIds) && {
      lastUsedSuccessCriteriaIds: successCriteriaIds,
    }),
  };
  return await patchApi(
    baseUrl + '/users/last-used-settings',
    preferences,
    options,
  );
};

export const getAssignmentStatusById = async (assignmentId) =>
  await getApi(baseUrl + '/assignments/' + assignmentId + '/stats');

const mapShareTypeToItemType = (shareType) => {
  if (shareType === 'commentBank') {
    return 'COMMENT_BANK';
  } else if (shareType === 'markingCriteria') {
    return 'MARKING_TEMPLATE';
  } else if (shareType === 'successCriteria') {
    return 'SUCCESS_CRITERIA';
  } else if (shareType === 'shortAnswer') {
    return 'SHORT_ANSWERS';
  } else if (shareType === 'task') {
    return 'TASK';
  }
  throw new Error(
    `Invalid share type: ${shareType}. Must be one of: commentBank, markingCriteria, successCriteria, shortAnswer, task`,
  );
};

export const shareResource = async (
  documentIds,
  shareType,
  recipientUserIds,
) => {
  const idsArray = Array.isArray(documentIds) ? documentIds : [documentIds];
  const itemType = mapShareTypeToItemType(shareType);

  if (idsArray.length === 0) {
    throw new Error('Item list cannot be empty');
  }

  if (!Array.isArray(recipientUserIds) || recipientUserIds.length === 0) {
    throw new Error('Recipient user IDs list cannot be empty');
  }

  const response = await postApi(
    `${baseUrl}/api/shares`,
    {
      itemIds: idsArray,
      itemType: itemType,
      recipientUserIds: recipientUserIds,
    },
    { suppressToast: true, includeMessages: true },
  );

  return {
    data: response?.data,
    successMessage: response?.successMessage,
    errorMessage: response?.errorMessage,
  };
};

export const shareMultipleMarkingTools = async (
  documents,
  recipientUserIds,
) => {
  if (!Array.isArray(documents) || documents.length === 0) {
    throw new Error('Documents list cannot be empty');
  }

  if (!Array.isArray(recipientUserIds) || recipientUserIds.length === 0) {
    throw new Error('Recipient user IDs list cannot be empty');
  }

  const groupedByType = documents.reduce((acc, doc) => {
    const itemType = mapShareTypeToItemType(doc.shareType);
    if (!acc[itemType]) {
      acc[itemType] = [];
    }
    acc[itemType].push(doc.id);
    return acc;
  }, {});

  const sharePromises = Object.entries(groupedByType).map(
    async ([itemType, itemIds]) => {
      return await postApi(
        `${baseUrl}/api/shares`,
        {
          itemIds: itemIds,
          itemType: itemType,
          recipientUserIds: recipientUserIds,
        },
        { suppressToast: true, includeMessages: true },
      );
    },
  );

  const results = await Promise.all(sharePromises);
  const successMessages = results.map((r) => r?.successMessage).filter(Boolean);
  const errorMessages = results.map((r) => r?.errorMessage).filter(Boolean);

  return {
    data: results.map((r) => r?.data),
    successMessage:
      successMessages.length > 0 ? successMessages.join(' ') : null,
    errorMessage: errorMessages.length > 0 ? errorMessages.join(' ') : null,
  };
};

export const shareTasks = async (documentIds, recipientUserIds) => {
  const idsArray = Array.isArray(documentIds) ? documentIds : [documentIds];

  if (idsArray.length === 0) {
    throw new Error('Item list cannot be empty');
  }

  if (!Array.isArray(recipientUserIds) || recipientUserIds.length === 0) {
    throw new Error('Recipient user IDs list cannot be empty');
  }

  return await postApi(`${baseUrl}/api/shares`, {
    itemIds: idsArray,
    itemType: 'TASK',
    recipientUserIds: recipientUserIds,
  });
};
export const getShareDetails = async (shareId) => {
  return await getApi(`${baseUrl}/api/shares/${shareId}`);
};

// JED-1279: what a pending share hands over, for its recipient's preview. Only
// task shares are served; a task's own GET /assignments/{id} is closed to
// teachers outside its classes. Rejects (404) for anyone but the recipient.
export const getSharedItem = async (shareId) => {
  return await getApi(
    `${baseUrl}/api/shares/${encodeURIComponent(shareId)}/item`,
  );
};

export const acceptShare = async (shareId) => {
  return await postApi(`${baseUrl}/api/shares/${shareId}/accept`, {});
};

export const rejectShare = async (shareId) => {
  return await postApi(`${baseUrl}/api/shares/${shareId}/reject`, {});
};

export const sendReferralInvites = async (emails) => {
  try {
    const userId = localStorage.getItem('userId');
    const requestBody = {
      referrerId: userId,
      referralEmails: emails,
    };

    const response = await postApi(`${baseUrl}/referral/send`, requestBody, { throwOnError: true, suppressToast: true });
    return response;
  } catch (error) {
    console.error('Error sending referral invites:', error);
    throw error;
  }
};

export const getReferralStatus = async () =>
  await getApi(`${baseUrl}/referral/couponCode`);

export const getShares = async (id = null) => {
  const queryParams = new URLSearchParams();
  const isObjectId = id != null && typeof id === 'object';
  const documentType = isObjectId ? (id.documentType ?? null) : id;
  const subjectCode = isObjectId ? (id.subjectCode ?? null) : null;
  const subjectCodes = isObjectId ? (id.subjectCodes ?? null) : null;
  const subject = isObjectId ? (id.subject ?? null) : null;
  const subjects = isObjectId ? (id.subjects ?? null) : null;
  const status = isObjectId ? (id.status ?? null) : null;
  if (documentType) queryParams.append('documentType', documentType);
  const codeList =
    (Array.isArray(subjectCodes)
      ? subjectCodes
      : subjectCodes
        ? [subjectCodes]
        : []) ??
    (subjectCode ? [subjectCode] : []) ??
    (Array.isArray(subjects) ? subjects : subjects ? [subjects] : []) ??
    (subject ? [subject] : []);
  const normalized = codeList
    .filter(Boolean)
    .map((s) => (String(s).toLowerCase() === 'unassigned' ? 'UNASSIGNED' : s));
  if (normalized.length === 1) queryParams.append('subjectCode', normalized[0]);
  else normalized.forEach((c) => queryParams.append('subjectCodes', c));
  if (status) queryParams.append('status', status);
  const url = `${baseUrl}/api/shares${
    queryParams.toString() ? '?' + queryParams.toString() : ''
  }`;
  const data = await getApi(url);
  return data;
};

export const updateTasksViewedCount = async () => {
  return await patchApi(`${baseUrl}/users/tasks/viewed`, {});
};

export const updateMarkingTemplatesViewedCount = async () => {
  return await patchApi(`${baseUrl}/users/marking-templates/viewed`, {});
};

export const updateCommentBanksViewedCount = async () => {
  return await patchApi(`${baseUrl}/users/comment-banks/viewed`, {});
};

export const updateSuccessCriteriasViewedCount = async () => {
  return await patchApi(`${baseUrl}/users/success-criterias/viewed`, {});
};

export const updateMarkingToolsViewedCount = async () => {
  return await patchApi(
    `${baseUrl}/users/marking-templates-and-comment-banks/viewed`,
    {},
  );
};

export const updateShortAnswersViewedCount = async () => {
  return await patchApi(`${baseUrl}/users/short-answers/viewed`, {});
};

export const updateSuccessCriteriaViewedCount = async () => {
  return await patchApi(`${baseUrl}/users/success-criteria/viewed`, {});
};

export const getAllShortAnswers = async (filters = {}) => {
  if (!isLoggedIn()) {
    return [];
  }
  if (!isTeacher()) {
    return [];
  }
  return await getApi(
    baseUrl + '/teachers/shortAnswers' + buildQueryString(filters),
  );
};

export const getShortAnswer = async (id) => {
  if (!isLoggedIn()) {
    return null;
  }
  if (!isTeacher()) {
    return null;
  }
  if (id) {
    return await getApi(baseUrl + '/teachers/shortAnswers/' + id);
  }
  return await getApi(baseUrl + '/teachers/shortAnswers');
};

export const createShortAnswer = async (shortAnswer) => {
  console.log('[copy short answer] API request', {
    url: baseUrl + '/teachers/shortAnswers',
    payload: shortAnswer,
  });
  const res = await postApi(baseUrl + '/teachers/shortAnswers', shortAnswer);
  console.log('[copy short answer] API response', res);
  return res;
};

export const updateShortAnswer = async (id, shortAnswer, options = {}) => {
  return await patchApi(
    baseUrl + '/teachers/shortAnswers/' + id,
    shortAnswer,
    options
  );
};

export const deleteShortAnswer = async (id) => {
  await deleteApi(baseUrl + '/teachers/shortAnswers/' + id);
};

export const getAllShortAnswersWithPreferences = async () => {
  if (!isLoggedIn()) {
    return [];
  }
  if (!isTeacher()) {
    return [];
  }
  const response = await getApi(baseUrl + '/teachers/shortAnswers/preferences');
  return response?.criteria || [];
};

export const updateShortAnswerPreferences = async (operation, data) => {
  const requestData = {
    operation,
    ...data,
  };
  const response = await patchApi(
    baseUrl + '/teachers/shortAnswers/preferences',
    requestData,
  );
  return response;
};

export const getShortAnswerPreferences = async () => {
  if (!isLoggedIn()) {
    return [];
  }
  const response = await getApi(
    baseUrl + '/teachers/classes/shortAnswers/preferences',
  );
  return response || [];
};

// Item Settings Update Functions
export const updateMarkingCriteriaSettings = async (id, settings) => {
  return await patchApi(
    baseUrl + '/teachers/markingCriteria/' + id + '/settings',
    settings,
  );
};

export const updateCommentBankSettings = async (id, settings) => {
  return await patchApi(
    baseUrl + '/commentbanks/' + id + '/settings',
    settings,
  );
};

export const updateSuccessCriteriaSettings = async (id, settings) => {
  return await patchApi(
    baseUrl + '/teachers/successCriteria/' + id + '/settings',
    settings,
  );
};

export const updateShortAnswerSettings = async (id, settings) => {
  return await patchApi(
    baseUrl + '/teachers/shortAnswers/' + id + '/settings',
    settings,
  );
};

const catalogListOf = (value) =>
  Array.isArray(value) ? value : value == null || value === '' ? [] : [value];

const catalogPrimitiveText = (value) =>
  typeof value === 'string' || typeof value === 'number'
    ? String(value).trim()
    : '';

const catalogFirstText = (...values) => {
  for (const value of values) {
    const normalized = catalogPrimitiveText(value);
    if (normalized) return normalized;
  }
  return '';
};

const catalogSubjectCodeOf = (subject) =>
  subject && typeof subject === 'object'
    ? catalogFirstText(subject.subjectCode, subject.code)
    : '';

const catalogStateCodeOf = (state) => {
  const code =
    state && typeof state === 'object'
      ? catalogFirstText(state.code, state.value, state.state)
      : catalogPrimitiveText(state);
  return code.toUpperCase();
};

const normalizeYearNum = (year) => {
  const normalized = catalogPrimitiveText(year)
    .replace(/^Year-?\s*/i, '')
    .trim();
  if (!/^\d+$/.test(normalized)) return null;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) && parsed >= 1 && parsed <= 13 ? parsed : null;
};

const catalogTaskTypeCodeOf = (taskType) =>
  taskType && typeof taskType === 'object'
    ? catalogFirstText(
        taskType.code,
        taskType.taskType,
        taskType.value,
        taskType.name
      )
    : catalogPrimitiveText(taskType);

const catalogTaskTypeLabelOf = (taskType) =>
  taskType && typeof taskType === 'object'
    ? catalogFirstText(
        taskType.name,
        taskType.title,
        taskType.label,
        taskType.taskType,
        taskType.value,
        taskType.code
      )
    : catalogPrimitiveText(taskType);

const catalogResponseTypeCodeOf = (responseType) =>
  responseType && typeof responseType === 'object'
    ? catalogFirstText(responseType.code, responseType.value, responseType.name)
    : catalogPrimitiveText(responseType);

const catalogResponseTypeLabelOf = (responseType) =>
  responseType && typeof responseType === 'object'
    ? catalogFirstText(
        responseType.name,
        responseType.label,
        responseType.title,
        responseType.value,
        responseType.code
      )
    : catalogPrimitiveText(responseType);

const catalogMatchesState = (declaredStates, requestedState) => {
  const declared = catalogListOf(declaredStates);
  if (declared.length === 0) return true;
  if (!requestedState) return false;
  return declared.some((state) => {
    const normalized = catalogStateCodeOf(state);
    return Boolean(normalized) && normalized === requestedState;
  });
};

const catalogMatchesYear = (declaredYears, requestedYear) => {
  const declared = catalogListOf(declaredYears);
  if (declared.length === 0) return true;
  if (requestedYear == null) return false;
  return declared.some((year) => normalizeYearNum(year) === requestedYear);
};

const matchingCatalogSubjects = (apiSubjects, subjectCode, state, year) => {
  const requestedSubjectCode = catalogPrimitiveText(subjectCode);
  if (!requestedSubjectCode) return [];
  const requestedState = catalogStateCodeOf(state);
  const requestedYear = normalizeYearNum(year);
  return (Array.isArray(apiSubjects) ? apiSubjects : []).filter(
    (subject) =>
      catalogSubjectCodeOf(subject) === requestedSubjectCode &&
      catalogMatchesState(subject.states, requestedState) &&
      catalogMatchesYear(subject.years, requestedYear)
  );
};

export const getTaskTypesForSubject = async (subjectCode, state, year) => {
  const apiSubjects = await getSubjectsFromClasses();
  const taskTypes = [];
  const seen = new Set();
  matchingCatalogSubjects(apiSubjects, subjectCode, state, year).forEach(
    (subject) => {
      catalogListOf(subject.taskTypes).forEach((taskType) => {
        const code = catalogTaskTypeCodeOf(taskType);
        if (!code || seen.has(code)) return;
        seen.add(code);
        taskTypes.push({
          code,
          name: catalogTaskTypeLabelOf(taskType) || code,
          tooltip: '',
        });
      });
    }
  );
  return { taskTypes };
};

export const getResponseTypesForSubject = async (
  subjectCode,
  taskType,
  state,
  year
) => {
  const apiSubjects = await getSubjectsFromClasses();
  const requestedTaskType = catalogTaskTypeCodeOf(taskType);
  const responseTypes = [];
  const seen = new Set();
  matchingCatalogSubjects(apiSubjects, subjectCode, state, year).forEach(
    (subject) => {
      catalogListOf(subject.taskTypes).forEach((entry) => {
        if (
          !requestedTaskType ||
          catalogTaskTypeCodeOf(entry) !== requestedTaskType ||
          !entry ||
          typeof entry !== 'object'
        ) {
          return;
        }
        catalogListOf(entry.responseTypes).forEach((responseType) => {
          const code = catalogResponseTypeCodeOf(responseType);
          if (!code || seen.has(code)) return;
          seen.add(code);
          responseTypes.push({
            code,
            name: catalogResponseTypeLabelOf(responseType) || code,
            tooltip:
              responseType && typeof responseType === 'object'
                ? catalogPrimitiveText(responseType.tooltip)
                : '',
          });
        });
      });
    }
  );
  return { responseTypes };
};

export const getStates = async () => {
  return await getApi(`${baseUrl}/api/v1/subjects/states`);
};

// ==========================================
// Class Subjects API (/classes)
// ==========================================

export const getClassSubjects = async (classId) => {
  if (!classId) return [];
  return await getApi(`${baseUrl}/classes/${classId}/subjects`);
};

export const updateClassSubjects = async (classId, subjects) => {
  if (!classId) return null;
  return await patchApi(`${baseUrl}/classes/${classId}/subjects`, subjects);
};

export const addSubjectToClass = async (classId, subject) => {
  if (!classId) return null;
  return await postApi(`${baseUrl}/classes/${classId}/subjects`, subject);
};

export const removeSubjectFromClass = async (classId, subjectCode) => {
  if (!classId || !subjectCode) return null;
  return await deleteApi(
    `${baseUrl}/classes/${classId}/subjects/${subjectCode}`,
  );
};

export const checkSubjectSelectionRequired = async (classId) => {
  if (!classId) return null;
  return await getApi(
    `${baseUrl}/classes/${classId}/subject-selection-required`,
  );
};

export const checkSubjectSelectionForMultipleClasses = async (classIds) => {
  if (!classIds || classIds.length === 0) return [];
  return await postApi(
    `${baseUrl}/classes/subject-selection-required`,
    classIds,
  );
};

const toValidNumbers = (arr) =>
  (arr || [])
    .map(Number)
    .filter((n) => !Number.isNaN(n) && n !== 0 && n != null);

export const buildStatsFilterBody = (filters) => ({
  classIds: (filters.classIds || []).filter(Boolean),
  taskTypes: (filters.taskTypes || []).filter(Boolean),
  taskIds: (filters.taskIds || []).filter(Boolean),
  startDate: filters.startDate || undefined,
  endDate: filters.endDate || undefined,
  studentIds: (filters.studentIds || [])
    .map((student) => student?.id ?? student)
    .filter(Boolean)
    .map(String),
  markingToolIds: (filters.markingToolIds || []).filter(Boolean),
  sortBy: filters.sortBy || undefined,
  sortOrder: filters.sortOrder || undefined,
  subjectCodes: (filters.subjectCodes || [])
    .map((subject) => subject?.id ?? subject)
    .filter(Boolean)
    .map(String),
});

const emptyCatalog = () => ({
  classes: [],
  subjects: [],
  taskTypes: [],
  taskNames: [],
  students: [],
});

export const fetchCatalog = async (filters) => {
  const body = buildStatsFilterBody(filters);
  const result = await postApi(`${baseUrl}/api/stats/catalog`, body, {
    suppressToast: true,
  });
  // `unavailable` lets an empty view tell a failed request apart from a teacher with no classes.
  return result || { ...emptyCatalog(), unavailable: true };
};

export const fetchSubmissionsStats = async (filters) => {
  const body = buildStatsFilterBody(filters);
  const result = await postApi(`${baseUrl}/api/stats/submissions`, body, {
    suppressToast: true,
  });
  return (
    result || {
      total: 0,
      timeSavedMinutes: 0,
      timeSavedHours: 0,
      classwork: 0,
      independent: 0,
      onTimePercent: 0,
      taskCompletionPercentage: 0,
      unavailable: true,
    }
  );
};

export const fetchCommentBanksStats = async (filters) => {
  const body = buildStatsFilterBody(filters);
  const result = await postApi(
    `${baseUrl}/api/stats/markingTools/commentBanks`,
    body,
    { suppressToast: true },
  );
  return result || { options: [], stats: [] };
};

export const fetchSuccessCriteriaStats = async (filters) => {
  const body = buildStatsFilterBody(filters);
  const result = await postApi(
    `${baseUrl}/api/stats/markingTools/successCriterias`,
    body,
    {
      suppressToast: true,
    },
  );
  return result || { options: [], stats: [] };
};

export const fetchRubricsStats = async (filters) => {
  const body = buildStatsFilterBody(filters);
  const result = await postApi(
    `${baseUrl}/api/stats/markingTools/rubrics`,
    body,
    {
      suppressToast: true,
    },
  );
  return result || { options: [], stats: [] };
};

export const fetchRubricsPercentageStats = async (filters) => {
  const body = buildStatsFilterBody(filters);
  const result = await postApi(
    `${baseUrl}/api/stats/markingTools/rubricsPercentage`,
    body,
    {
      suppressToast: true,
    },
  );
  return result || [];
};

export const getTeachersShortAnswers = async (filters = {}) => {
  if (!isLoggedIn() || !isTeacher()) return { shortAnswers: [] };
  const response = await getApi(
    baseUrl + '/teachers/shortAnswers' + buildQueryString(filters),
  );
  const list = Array.isArray(response) ? response : [];
  return { shortAnswers: list };
};

export const fetchShortAnswerStats = async (filters) => {
  const body = buildStatsFilterBody(filters);
  const result = await postApi(
    `${baseUrl}/api/stats/markingTools/shortAnswers`,
    body,
    {
      suppressToast: true,
    },
  );
  return result || { options: [], stats: [] };
};

// --- Onboarding/setup-checklist service functions (teacher-onboarding branch) ---

export const getClassesWithStudents = async () => {
  const a = isLoggedIn();
  if (a) {
    const classes = await getApi(baseUrl + '/classes/all/details');

    const processedClasses = classes?.map((cls) => cls) || [];

    return processedClasses;
  }
  return [];
};

export const getActiveClassesWithStudents = async () => {
  const { filterActiveClasses } = await import('./utils/archiveFilter');
  const all = await getClassesWithStudents();
  return filterActiveClasses(all);
};

export const getSharedWithMeDocuments = async () => {
  if (isLoggedIn()) {
    return await getApi(baseUrl + '/submissions/sharedWithMe');
  } else {
    return [];
  }
};

export const submitClassChangeRequest = async (formData) => {
  return await postApi(
    baseUrl + '/class-change/class-change-request',
    {
      requestedChanges: formData.requestedChanges,
    },
    { includeMessages: true },
  );
};

export const saveSessionBooking = async (sessionBooking) =>
  await patchApi(baseUrl + '/users/profile', { sessionBooking });

// Setup-checklist persistence (school-teacher onboarding flow). Returns
// `{ userId, tasks: boolean[] | null, updatedAt: string | null }` shape;
// `tasks: null` means the user has never persisted state, treat as empty.
export const getSetupChecklist = async () => {
  if (!isLoggedIn()) {
    return null;
  }
  return await getApi(baseUrl + '/users/setupChecklist');
};

export const updateSetupChecklist = async (tasks) =>
  await patchApi(baseUrl + '/users/setupChecklist', { tasks });

// ==================== AI QUESTION GENERATION ====================

const questionUrl = (assignmentId, serialNumber, action) =>
  `${baseUrl}/assignments/${assignmentId}/questions/${serialNumber}/${action}`;

// Returns an unsaved draft Question; the server never writes. Accepting it is a
// normal title + assignment save.
// parts ⊆ 'QUESTION' | 'STIMULUS_TEXT' | 'STIMULUS_IMAGE', defaulting to all;
// `current` is kept for the parts not listed.
// A failure rejects with questionGenerationError (utils/questionGeneration):
// its `message` is ready to show, and `code`/`status` say which refusal it was
// (429 QUESTION_GENERATION_DAILY_LIMIT, 503 QUESTION_GENERATION_DISABLED). The
// refusal is read from postApi's includeMessages envelope, as askJeddAI reads
// INSUFFICIENT_CREDITS; those codes never get modifyData's generic toast.
export const generateQuestion = async (
  assignmentId,
  serialNumber,
  { settings, current, parts },
  options = {}
) => {
  const response = await postApi(
    questionUrl(assignmentId, serialNumber, 'generate'),
    { settings, current, parts },
    {
      ...options,
      throwOnError: false,
      includeMessages: true,
      quietErrorCodes: QUESTION_GENERATION_REFUSALS,
    }
  );
  if (response?.data) return response.data;
  throw questionGenerationError(response);
};
